<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import ArgonAlert from "@/components/ArgonAlert.vue";
import { ROLES } from "@/services/acl";
import {
  fetchAllConfigs,
  fetchEmailTemplates,
  updateConfigValue,
  updateEmailTemplate,
  fetchProfileAttributes,
  updateProfileAttribute,
  fetchPlans,
  upsertPlan,
  reloadPromptLibrary,
} from "@/services/backofficeClient";

const store = useStore();
const canEditConfig = computed(() => store.getters.hasRole(ROLES.CONFIG_EDIT));

const TAB_ITEMS = [
  { key: "brand", label: "Brand" },
  { key: "adminNotification", label: "Admin Notification" },
  { key: "technical", label: "Technical" },
  { key: "media", label: "Media" },
  { key: "communication", label: "Communication" },
  { key: "promptLibrary", label: "Prompt Library" },
  { key: "customerSupport", label: "Customer Support" },
  { key: "emailManagement", label: "Email Inbox" },
  { key: "complianceAiResponse", label: "Complaint AI" },
  { key: "verificationAi", label: "Verification AI" },
  { key: "referral", label: "Referral" },
  { key: "payments", label: "Payments" },
  { key: "plans", label: "Plans" },
  { key: "emailTemplates", label: "Email templates" },
  { key: "profileAttributes", label: "Profile attributes" },
];

const STRING_KEYS = [
  "appName",
  "logoUrl",
  "defaultLanguage",
  "notifyAdminEmail",
  "notifyAdminTelegramBotApiKey",
  "notifyAdminTelegramChatId",
  "notifyAdminSlackWebhookUrl",
  "notifyAdminGoogleChatWebhookUrl",
  "smsFrom",
  "emailFrom",
  "emailFromName",
  "mapBoxToken",
  "openAiApiKey",
  "configApiToken",
  "aiComplaintOpenAiModel",
  "telegramSignInBotApiKey",
  "telegramSignInBotWebhookSecretKey",
  "iranPayamakApiKey",
  "iranPayamakTemplateCode",
  "iranPayamakCodeParameterName",
  "smsIrApiKey",
  "smsIrTemplateId",
  "smsIrCodeParameterName",
  "referralRewardProductId",
  "emailManagementWebhookSecret",
  "emailManagementWebhookAuthorization",
];

const NUMERIC_KEYS = [
  "maxImageSizeInBytes",
  "maxVoiceSizeInBytes",
  "referralMaxReferCount",
  "referralRewardProductDuration",
  "exchangeRate",
  "telegramStarsUsdCentsPerStar",
  "geocoderMinPopulation",
];

const BOOLEAN_KEYS = [
  "referralVerificationRequired",
  "referralFacePhotoRequired",
  "useOpenSearchForMatching",
  "useOpenSearchForGeocoding",
];

const MESSAGE_KEYS = [
  "welcomeMessageNotification",
  "verificationAcceptedMessageNotification",
  "verificationRejectedCodeMessageNotification",
  "verificationRejectedSelfieMessageNotification",
  "verificationRejectedGestureMessageNotification",
  "verificationRejectedAgeMessageNotification",
  "walletTopUpMessageNotification",
  "walletTopUpRejectedMessageNotification",
  "planPurchaseMessageNotification",
  "warningMessageNotification",
];

const PAYABLE_FEATURES = [
  "match_react",
  "match_like",
  "match_super_like",
  "match_dislike",
  "match_direct_message",
  "match_undo_set_relation",
  "match_get_user_list",
  "match_see_who_liked_me",
  "setting_change_address",
  "app_hide_ads_1",
  "app_hide_ads_2",
  "app_hide_ads_3",
  "app_hide_ads_4",
  "app_hide_ads_5",
  "user_see_active_status",
  "match_filter_active_users",
  "match_filter_verified_users",
  "match_filter_countries",
  "chat_see_read_receipt",
  "match_ghost_mode",
  "app_show_liked_me_users",
  "app_show_liked_users",
  "app_show_disliked_users",
  "app_show_blocked_users",
];

const DEFAULT_LANGUAGE_SAMPLES = ["en", "fa"];
const DEFAULT_CHANNELS = ["email", "telegram", "slack", "googlechat"];

const PROMPT_LIBRARY_FILES = [
  { label: "Customer support", path: "library/SUPPORT.md" },
  { label: "Customer support suggestion", path: "library/SUPPORT_SUGGESTION.md" },
  { label: "Complaint review", path: "library/COMPLAINT.md" },
  { label: "Admin translate text", path: "library/ADMIN_TRANSLATE_TEXT.md" },
  { label: "Admin translate texts", path: "library/ADMIN_TRANSLATE_TEXTS.md" },
  { label: "Gesture verification", path: "library/VERIFICATION.md" },
  { label: "Email context", path: "library/EMAIL.md" },
  { label: "Email suggestion", path: "library/EMAIL_SUGGESTION.md" },
];

const activeTab = ref(TAB_ITEMS[0].key);
const isLoading = ref(true);
const errorMessage = ref("");
const statusMessage = ref("");
const statusType = ref("success");

const serverValues = reactive({});
const draftValues = reactive({});
const savingKeys = reactive({});
const isReloadingPrompts = ref(false);

const notifyEventInput = ref("");
const newLanguageInput = ref("");
const messageLanguageInputs = reactive({});
const emailTemplates = ref([]);
const emailTemplateNames = ref([]);
const emailTemplateLanguages = ref([]);
const selectedTemplateName = ref("");
const selectedTemplateLanguage = ref("");
const emailTemplate = reactive({
  subject: "",
  textBody: "",
  htmlBody: "",
});
const emailTemplateServer = reactive({
  subject: "",
  textBody: "",
  htmlBody: "",
});
const isLoadingTemplate = ref(false);
const templateErrorMessage = ref("");
const templateStatusMessage = ref("");
const templateStatusType = ref("success");
const profileAttributes = ref([]);
const profileAttributesLoading = ref(false);
const profileAttributesError = ref("");
const selectedProfileAttribute = ref(null);
const profileAttributeDraft = reactive({
  description: "",
  matchWeight: "",
  profileQualityWeight: "",
  useForBioSuggestions: false,
  options: "",
});
const profileAttributeStatus = ref("");
const profileAttributeStatusType = ref("success");
const plans = ref([]);
const plansLoaded = ref(false);
const plansLoading = ref(false);
const plansError = ref("");
const plansStatus = ref("");
const plansStatusType = ref("success");
const planSaving = ref(false);
const isPlanModalOpen = ref(false);
const planDraft = reactive({
  id: "",
  name: {},
  productIds: [""],
  isActive: false,
  order: 5,
  matchingVisibilityScore: 500,
});
const editingPlanId = ref(null);
const planFormMessage = ref("");
const planFormMessageType = ref("success");
const selectedFeatureToAdd = ref("");
const isOffersModalOpen = ref(false);
const editingOffersPlanId = ref(null);
const offersDraft = reactive({
  offers: [],
});
const offersSaving = ref(false);
const offersFormMessage = ref("");
const offersFormMessageType = ref("success");
const isFeaturesModalOpen = ref(false);
const editingFeaturesPlanId = ref(null);
const featuresDraft = reactive({
  features: [],
});
const featuresSaving = ref(false);
const featuresFormMessage = ref("");
const featuresFormMessageType = ref("success");
const tabKeys = TAB_ITEMS.map((tab) => tab.key);
const route = useRoute();
const router = useRouter();

const deepClone = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => deepClone(item));
  }
  if (value && typeof value === "object") {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = deepClone(value[key]);
      return acc;
    }, {});
  }
  return value;
};

const normalizeString = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const normalizeNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.trunc(numeric) : null;
};

const normalizeBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }
  return false;
};

const normalizeStringList = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim().toLowerCase())
      .filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);
  }
  return [];
};

const normalizeMessageMap = (value) => {
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [lang, text]) => {
      acc[String(lang).toLowerCase()] = text?.toString() ?? "";
      return acc;
    }, {});
  }
  return {};
};

const normalizeEmailSenders = (value) => {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  return value.reduce((acc, item) => {
    const email = String(item?.email || "").trim().toLowerCase();
    if (!email || seen.has(email)) return acc;
    seen.add(email);
    const name = String(item?.name || "").trim();
    acc.push({ email, name: name || null });
    return acc;
  }, []);
};

const normalizeSmsIrParameters = (value) => {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  return value.reduce((acc, item) => {
    const name = String(item?.name || "").trim();
    if (!name || seen.has(name)) return acc;
    seen.add(name);
    acc.push({ name, value: String(item?.value ?? "") });
    return acc;
  }, []);
};

const normalizeDiscountPercent = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeNotifyChannels = (value) => {
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [event, channels]) => {
      const normalizedChannels = Array.isArray(channels)
        ? channels
          .map((channel) => String(channel || "").trim().toLowerCase())
          .filter(Boolean)
        : [];
      acc[String(event)] = normalizedChannels;
      return acc;
    }, {});
  }
  return {};
};

const normalizeSlackWebhookUrlsByEvent = (value) => {
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [event, url]) => {
      const trimmed = String(url || "").trim();
      if (trimmed) acc[String(event)] = trimmed;
      return acc;
    }, {});
  }
  return {};
};

const normalizeTelegramChatIdsByEvent = (value) => {
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [event, chatId]) => {
      const trimmed = String(chatId || "").trim();
      if (trimmed) acc[String(event)] = trimmed;
      return acc;
    }, {});
  }
  return {};
};

const normalizeGoogleChatWebhookUrlsByEvent = (value) => {
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [event, url]) => {
      const trimmed = String(url || "").trim();
      if (trimmed) acc[String(event)] = trimmed;
      return acc;
    }, {});
  }
  return {};
};

const deepEqual = (a, b) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, idx) => deepEqual(item, b[idx]));
  }
  if (a && b && typeof a === "object" && typeof b === "object") {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => deepEqual(a[key], b[key]));
  }
  return a === b;
};

const languageOptions = computed(() => {
  const pool = new Set(DEFAULT_LANGUAGE_SAMPLES);

  normalizeStringList(draftValues.languages || []).forEach((code) =>
    pool.add(code)
  );

  const defLang = normalizeString(draftValues.defaultLanguage || "")
    .trim()
    .toLowerCase();
  if (defLang) pool.add(defLang);

  MESSAGE_KEYS.forEach((key) => {
    const map = draftValues[key] || {};
    Object.keys(map || {}).forEach((lang) => {
      if (lang) pool.add(lang.toLowerCase());
    });
  });

  return Array.from(pool).sort();
});

const messageLanguages = (key) => {
  const map = draftValues[key] || {};
  return Object.keys(map).sort();
};

const notifyEvents = computed(() => {
  const value = draftValues.notifyAdminChannels || {};
  return Object.keys(value).sort();
});

const notifyChannels = computed(() => {
  const value = draftValues.notifyAdminChannels || {};
  const set = new Set(DEFAULT_CHANNELS);
  Object.values(value || {}).forEach((channels) => {
    (channels || []).forEach((c) => set.add(String(c).toLowerCase()));
  });
  return Array.from(set).sort();
});

const planLanguages = computed(() => {
  const langs = normalizeStringList(draftValues.languages || []);
  if (langs.length) return langs;
  const defLang = normalizeString(draftValues.defaultLanguage || "")
    .trim()
    .toLowerCase();
  return defLang ? [defLang] : ["en"];
});

const isEditingPlan = computed(() => Boolean(editingPlanId.value));

const availableFeatureOptions = computed(() =>
  PAYABLE_FEATURES.filter(
    (feature) =>
      !featuresDraft.features.some(
        (item) => item.payableFeature === feature
      )
  )
);

const hasChanges = (key) => !deepEqual(serverValues[key], draftValues[key]);
const isSavingKey = (key) => Boolean(savingKeys[key]);

const resetTemplateDraft = () => {
  emailTemplate.subject = "";
  emailTemplate.textBody = "";
  emailTemplate.htmlBody = "";
  emailTemplateServer.subject = "";
  emailTemplateServer.textBody = "";
  emailTemplateServer.htmlBody = "";
};

const applyTemplatePayload = (payload) => {
  const subject = payload?.subject?.toString() ?? "";
  const textBody = payload?.textBody?.toString() ?? "";
  const htmlBody = payload?.htmlBody?.toString() ?? "";
  emailTemplate.subject = subject;
  emailTemplate.textBody = textBody;
  emailTemplate.htmlBody = htmlBody;
  emailTemplateServer.subject = subject;
  emailTemplateServer.textBody = textBody;
  emailTemplateServer.htmlBody = htmlBody;
};

const hasTemplateChanges = computed(
  () =>
    emailTemplate.subject !== emailTemplateServer.subject ||
    emailTemplate.textBody !== emailTemplateServer.textBody ||
    emailTemplate.htmlBody !== emailTemplateServer.htmlBody
);

const parseOptionsMapToString = (map) => {
  if (!map || typeof map !== "object") return "";
  return Object.entries(map)
    .map(([k, v]) => `${k}: ${v}`)
    .join("\n");
};

const normalizeTabKey = (value) => {
  if (typeof value !== "string") return TAB_ITEMS[0].key;
  return tabKeys.includes(value) ? value : TAB_ITEMS[0].key;
};

const applyTabFromRoute = () => {
  const desired = normalizeTabKey(route.query?.tab);
  activeTab.value = desired;
};

const selectTab = (tabKey) => {
  const normalized = normalizeTabKey(tabKey);
  if (normalized === activeTab.value && route.query?.tab === normalized) return;
  activeTab.value = normalized;
  router.replace({
    name: "Config",
    query: { ...route.query, tab: normalized },
  });
};

const applyProfileAttribute = (attr) => {
  selectedProfileAttribute.value = attr;
  if (!attr) {
    profileAttributeDraft.description = "";
    profileAttributeDraft.matchWeight = "";
    profileAttributeDraft.profileQualityWeight = "";
    profileAttributeDraft.useForBioSuggestions = false;
    profileAttributeDraft.options = "";
    profileAttributeStatus.value = "";
    return;
  }
  profileAttributeDraft.description = attr.description || "";
  profileAttributeDraft.matchWeight =
    attr.matchWeight != null ? String(attr.matchWeight) : "";
  profileAttributeDraft.profileQualityWeight =
    attr.profileQualityWeight != null ? String(attr.profileQualityWeight) : "";
  profileAttributeDraft.useForBioSuggestions = Boolean(
    attr.useForBioSuggestions
  );
  profileAttributeDraft.options = parseOptionsMapToString(attr.options);
  profileAttributeStatus.value = "";
};

const assignValue = (key, value) => {
  draftValues[key] = value;
};

const prepareValueForRequest = (key) => {
  const value = draftValues[key];

  if (NUMERIC_KEYS.includes(key)) {
    return value === null || value === undefined || value === ""
      ? null
      : Number(value);
  }
  if (BOOLEAN_KEYS.includes(key)) {
    return Boolean(value);
  }
  if (key === "languages") {
    return normalizeStringList(value);
  }
  if (key === "notifyAdminChannels") {
    return normalizeNotifyChannels(value);
  }
  if (key === "notifyAdminSlackWebhookUrlsByEvent") {
    return normalizeSlackWebhookUrlsByEvent(value);
  }
  if (key === "notifyAdminTelegramChatIdsByEvent") {
    return normalizeTelegramChatIdsByEvent(value);
  }
  if (key === "notifyAdminGoogleChatWebhookUrlsByEvent") {
    return normalizeGoogleChatWebhookUrlsByEvent(value);
  }
  if (MESSAGE_KEYS.includes(key)) {
    return normalizeMessageMap(value);
  }
  if (key === "websiteGiftCardDiscountPercent") {
    return normalizeDiscountPercent(value);
  }
  if (key === "emailManagementSenders") {
    return normalizeEmailSenders(value);
  }
  if (key === "smsIrAdditionalParameters") {
    return normalizeSmsIrParameters(value);
  }
  if (typeof value === "string") {
    return value.trim();
  }
  return value;
};

const handleSave = async (key) => {
  if (isSavingKey(key)) return;
  statusMessage.value = "";
  statusType.value = "success";
  savingKeys[key] = true;

  try {
    const prepared = prepareValueForRequest(key);
    await updateConfigValue(key, prepared);
    serverValues[key] = deepClone(prepared);
    draftValues[key] = deepClone(prepared);
    statusMessage.value = `Saved ${key}`;
    statusType.value = "success";
  } catch (error) {
    console.error(`Failed to save ${key}`, error);
    statusMessage.value =
      error?.payload?.message ||
      error?.message ||
      `Failed to save ${key}. Please try again.`;
    statusType.value = "danger";
  } finally {
    savingKeys[key] = false;
  }
};

const handleReset = (key) => {
  draftValues[key] = deepClone(serverValues[key]);
};

const handleReloadPrompts = async () => {
  if (isReloadingPrompts.value) return;
  statusMessage.value = "";
  statusType.value = "success";
  isReloadingPrompts.value = true;

  try {
    await reloadPromptLibrary();
    statusMessage.value =
      "Prompts reloaded. The next request pulls them from the repository.";
    statusType.value = "success";
  } catch (error) {
    console.error("Failed to reload the prompt library", error);
    statusMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to reload the prompts. Please try again.";
    statusType.value = "danger";
  } finally {
    isReloadingPrompts.value = false;
  }
};

const normalizePayload = (payload) => {
  const normalized = {};

  Object.entries(payload || {}).forEach(([key, value]) => {
    normalized[key] = value;
  });

  STRING_KEYS.forEach((key) => {
    normalized[key] = normalizeString(normalized[key]);
  });

  NUMERIC_KEYS.forEach((key) => {
    normalized[key] = normalizeNumber(normalized[key]);
  });

  BOOLEAN_KEYS.forEach((key) => {
    normalized[key] = normalizeBoolean(normalized[key]);
  });

  normalized.languages = normalizeStringList(normalized.languages);
  normalized.notifyAdminChannels = normalizeNotifyChannels(
    normalized.notifyAdminChannels
  );
  normalized.notifyAdminSlackWebhookUrlsByEvent = normalizeSlackWebhookUrlsByEvent(
    normalized.notifyAdminSlackWebhookUrlsByEvent
  );
  normalized.notifyAdminTelegramChatIdsByEvent = normalizeTelegramChatIdsByEvent(
    normalized.notifyAdminTelegramChatIdsByEvent
  );

  MESSAGE_KEYS.forEach((key) => {
    normalized[key] = normalizeMessageMap(normalized[key]);
  });

  normalized.websiteGiftCardDiscountPercent = normalizeDiscountPercent(
    normalized.websiteGiftCardDiscountPercent
  );

  normalized.emailManagementSenders = normalizeEmailSenders(
    normalized.emailManagementSenders
  );

  normalized.smsIrAdditionalParameters = normalizeSmsIrParameters(
    normalized.smsIrAdditionalParameters
  );

  return normalized;
};

const applyTemplateFromList = () => {
  if (!selectedTemplateName.value || !selectedTemplateLanguage.value) {
    resetTemplateDraft();
    return;
  }
  const template = emailTemplates.value.find(
    (item) =>
      item?.name === selectedTemplateName.value &&
      item?.language === selectedTemplateLanguage.value
  );
  applyTemplatePayload(
    template || {
      name: selectedTemplateName.value,
      language: selectedTemplateLanguage.value,
      subject: "",
      textBody: "",
      htmlBody: "",
    }
  );
};

const loadEmailTemplates = async () => {
  isLoadingTemplate.value = true;
  templateErrorMessage.value = "";
  templateStatusMessage.value = "";
  try {
    const payload = await fetchEmailTemplates();
    const items = Array.isArray(payload?.items) ? payload.items : [];
    emailTemplates.value = items.map((item) => ({
      name: item?.name || "",
      language: (item?.language || "").toLowerCase(),
      subject: item?.subject || "",
      textBody: item?.textBody || "",
      htmlBody: item?.htmlBody || "",
      placeholders: Array.isArray(item?.placeholders)
        ? item.placeholders.map((p) => String(p))
        : [],
    }));

    const names = Array.from(
      new Set(emailTemplates.value.map((item) => item.name).filter(Boolean))
    );
    const langs = Array.from(
      new Set(
        emailTemplates.value
          .map((item) => (item.language || "").toLowerCase())
          .filter(Boolean)
      )
    );
    if (names.length) {
      emailTemplateNames.value = names;
      if (!selectedTemplateName.value || !names.includes(selectedTemplateName.value)) {
        selectedTemplateName.value = names[0];
      }
    }
    if (langs.length) {
      emailTemplateLanguages.value = langs;
      if (!selectedTemplateLanguage.value || !langs.includes(selectedTemplateLanguage.value)) {
        selectedTemplateLanguage.value = langs[0];
      }
    }
    applyTemplateFromList();
  } catch (error) {
    console.error("Failed to load email templates", error);
    resetTemplateDraft();
    templateErrorMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Unable to load email templates. Please try again.";
  } finally {
    isLoadingTemplate.value = false;
  }
};

const loadProfileAttributes = async () => {
  profileAttributesLoading.value = true;
  try {
    const payload = await fetchProfileAttributes();
    const items = Array.isArray(payload?.items) ? payload.items : [];
    profileAttributes.value = items;
    applyProfileAttribute(items[0] || null);
  } catch (error) {
    console.error("Failed to load profile attributes", error);
    profileAttributesError.value =
      error?.payload?.message ||
      error?.message ||
      "Unable to load profile attributes. Please try again.";
    profileAttributes.value = [];
    applyProfileAttribute(null);
  } finally {
    profileAttributesLoading.value = false;
  }
};

const saveProfileAttribute = async () => {
  const attr = selectedProfileAttribute.value;
  if (!attr?.name) return;
  profileAttributeStatus.value = "";
  profileAttributeStatusType.value = "success";
  profileAttributesError.value = "";
  try {
    const payload = await updateProfileAttribute(attr.name, {
      description: profileAttributeDraft.description,
      matchWeight: parseInt(profileAttributeDraft.matchWeight || "0", 10) || 0,
      profileQualityWeight:
        parseInt(profileAttributeDraft.profileQualityWeight || "0", 10) || 0,
      useForBioSuggestions: Boolean(profileAttributeDraft.useForBioSuggestions),
    });
    // refresh list
    const idx = profileAttributes.value.findIndex(
      (item) => item.name === attr.name
    );
    const updated = payload || attr;
    if (idx >= 0) {
      profileAttributes.value.splice(idx, 1, updated);
    }
    applyProfileAttribute(updated);
    profileAttributeStatus.value = "Profile attribute saved.";
    profileAttributeStatusType.value = "success";
  } catch (error) {
    console.error("Failed to save profile attribute", error);
    profileAttributeStatus.value =
      error?.payload?.message ||
      error?.message ||
      "Unable to save profile attribute. Please try again.";
    profileAttributeStatusType.value = "danger";
  } finally {
    await loadProfileAttributes(true);
  }
};

const saveEmailTemplate = async () => {
  if (!selectedTemplateName.value || !selectedTemplateLanguage.value) return;
  templateStatusMessage.value = "";
  templateErrorMessage.value = "";
  isLoadingTemplate.value = true;
  try {
    const payload = await updateEmailTemplate({
      name: selectedTemplateName.value,
      language: selectedTemplateLanguage.value,
      subject: emailTemplate.subject,
      textBody: emailTemplate.textBody,
      htmlBody: emailTemplate.htmlBody,
    });
    applyTemplatePayload(payload);
    const idx = emailTemplates.value.findIndex(
      (item) =>
        item.name === selectedTemplateName.value &&
        item.language === selectedTemplateLanguage.value
    );
    const updatedEntry = {
      name: selectedTemplateName.value,
      language: selectedTemplateLanguage.value,
      subject: emailTemplate.subject,
      textBody: emailTemplate.textBody,
      htmlBody: emailTemplate.htmlBody,
      placeholders: payload?.placeholders || [],
    };
    if (idx >= 0) {
      emailTemplates.value.splice(idx, 1, updatedEntry);
    } else {
      emailTemplates.value.push(updatedEntry);
    }
    templateStatusMessage.value = "Email template saved.";
    templateStatusType.value = "success";
  } catch (error) {
    console.error("Failed to save email template", error);
    templateErrorMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Unable to save template. Please try again.";
    templateStatusType.value = "danger";
  } finally {
    isLoadingTemplate.value = false;
  }
};

const resetEmailTemplate = () => {
  applyTemplatePayload(emailTemplateServer);
  templateStatusMessage.value = "";
  templateErrorMessage.value = "";
};

const loadConfig = async () => {
  isLoading.value = true;
  errorMessage.value = "";
  statusMessage.value = "";
  try {
    const payload = await fetchAllConfigs();
    if (!payload || typeof payload !== "object") {
      throw new Error("Unexpected configuration response.");
    }

    const normalized = normalizePayload(payload);
    Object.keys(serverValues).forEach((key) => delete serverValues[key]);
    Object.keys(draftValues).forEach((key) => delete draftValues[key]);

    Object.entries(normalized).forEach(([key, value]) => {
      serverValues[key] = deepClone(value);
      draftValues[key] = deepClone(value);
    });
    emailTemplateLanguages.value = deepClone(
      normalizeStringList(normalized.languages || [])
    );
  } catch (error) {
    console.error("Failed to load configuration", error);
    errorMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Unable to load configuration. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

const addLanguage = (code) => {
  const normalized = normalizeString(code || "").toLowerCase();
  if (!normalized) return;
  const current = normalizeStringList(draftValues.languages || []);
  if (current.includes(normalized)) return;
  assignValue("languages", [...current, normalized].sort());
  newLanguageInput.value = "";
};

const newSenderEmail = ref("");
const newSenderName = ref("");

const addEmailSender = () => {
  const email = newSenderEmail.value.trim().toLowerCase();
  if (!email) return;
  const current = normalizeEmailSenders(draftValues.emailManagementSenders || []);
  if (current.some((sender) => sender.email === email)) return;
  assignValue("emailManagementSenders", [
    ...current,
    { email, name: newSenderName.value.trim() || null },
  ]);
  newSenderEmail.value = "";
  newSenderName.value = "";
};

const removeEmailSender = (email) => {
  const current = normalizeEmailSenders(draftValues.emailManagementSenders || []);
  assignValue(
    "emailManagementSenders",
    current.filter((sender) => sender.email !== email)
  );
};

const updateEmailSenderName = (email, name) => {
  const current = normalizeEmailSenders(draftValues.emailManagementSenders || []);
  assignValue(
    "emailManagementSenders",
    current.map((sender) =>
      sender.email === email ? { ...sender, name: name.trim() || null } : sender
    )
  );
};

const newSmsIrParamName = ref("");
const newSmsIrParamValue = ref("");

const addSmsIrParameter = () => {
  const name = newSmsIrParamName.value.trim();
  if (!name) return;
  const current = normalizeSmsIrParameters(
    draftValues.smsIrAdditionalParameters || []
  );
  if (current.some((param) => param.name === name)) return;
  assignValue("smsIrAdditionalParameters", [
    ...current,
    { name, value: newSmsIrParamValue.value },
  ]);
  newSmsIrParamName.value = "";
  newSmsIrParamValue.value = "";
};

const removeSmsIrParameter = (name) => {
  const current = normalizeSmsIrParameters(
    draftValues.smsIrAdditionalParameters || []
  );
  assignValue(
    "smsIrAdditionalParameters",
    current.filter((param) => param.name !== name)
  );
};

const updateSmsIrParameterValue = (name, value) => {
  const current = normalizeSmsIrParameters(
    draftValues.smsIrAdditionalParameters || []
  );
  assignValue(
    "smsIrAdditionalParameters",
    current.map((param) => (param.name === name ? { ...param, value } : param))
  );
};

const removeLanguage = (code) => {
  const current = normalizeStringList(draftValues.languages || []);
  assignValue(
    "languages",
    current.filter((lang) => lang !== code.toLowerCase())
  );
};

const addMessageLanguage = (key, language) => {
  const code = normalizeString(language || "").toLowerCase();
  if (!code) return;
  const map = { ...(draftValues[key] || {}) };
  if (map[code] !== undefined) return;
  map[code] = "";
  assignValue(key, map);
  messageLanguageInputs[key] = "";
};

const removeMessageLanguage = (key, language) => {
  const map = { ...(draftValues[key] || {}) };
  delete map[language];
  assignValue(key, map);
};

const addNotifyEvent = () => {
  const name = notifyEventInput.value.trim();
  if (!name) return;
  const map = { ...(draftValues.notifyAdminChannels || {}) };
  if (!map[name]) {
    map[name] = [];
    assignValue("notifyAdminChannels", map);
  }
  notifyEventInput.value = "";
};

const toggleNotifyChannel = (event, channel, enabled) => {
  const map = { ...(draftValues.notifyAdminChannels || {}) };
  const channels = Array.isArray(map[event]) ? [...map[event]] : [];
  const normalizedChannel = String(channel).toLowerCase();
  if (enabled) {
    if (!channels.includes(normalizedChannel)) {
      channels.push(normalizedChannel);
    }
  } else {
    const idx = channels.indexOf(normalizedChannel);
    if (idx >= 0) channels.splice(idx, 1);
  }
  map[event] = channels;
  assignValue("notifyAdminChannels", map);
};

const setSlackWebhookOverride = (event, value) => {
  const map = { ...(draftValues.notifyAdminSlackWebhookUrlsByEvent || {}) };
  const trimmed = String(value || "").trim();
  if (trimmed) {
    map[event] = trimmed;
  } else {
    delete map[event];
  }
  assignValue("notifyAdminSlackWebhookUrlsByEvent", map);
};

const setTelegramChatIdOverride = (event, value) => {
  const map = { ...(draftValues.notifyAdminTelegramChatIdsByEvent || {}) };
  const trimmed = String(value || "").trim();
  if (trimmed) {
    map[event] = trimmed;
  } else {
    delete map[event];
  }
  assignValue("notifyAdminTelegramChatIdsByEvent", map);
};

const setGoogleChatWebhookOverride = (event, value) => {
  const map = { ...(draftValues.notifyAdminGoogleChatWebhookUrlsByEvent || {}) };
  const trimmed = String(value || "").trim();
  if (trimmed) {
    map[event] = trimmed;
  } else {
    delete map[event];
  }
  assignValue("notifyAdminGoogleChatWebhookUrlsByEvent", map);
};

const numericInputHandler = (key, value) => {
  const parsed = value === "" ? null : Number(value);
  assignValue(key, Number.isFinite(parsed) ? parsed : null);
};

const formatPayableFeature = (value) => {
  if (!value) return "";
  return value
    .toString()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizePlanFeature = (feature) => {
  return {
    payableFeature: normalizeString(feature?.payableFeature || "").trim(),
    windowLimit: normalizeNumber(feature?.windowLimit) ?? 0,
    windowDuration: normalizeString(feature?.windowDuration || "").trim(),
  };
};

const normalizeOfferPrice = (price) => {
  return {
    currencyCode: normalizeString(price?.currencyCode || "").trim(),
    price: normalizeNumber(price?.price) ?? 0,
  };
};

const normalizePlanOffer = (offer) => {
  return {
    id: normalizeString(offer?.id || "").trim(),
    title: normalizeString(offer?.title || ""),
    description: normalizeString(offer?.description || ""),
    prices: Array.isArray(offer?.prices)
      ? offer.prices.map(normalizeOfferPrice)
      : [],
    subscriptionPeriod: normalizeString(offer?.subscriptionPeriod || "").trim(),
  };
};

const normalizePlan = (plan) => {
  const name = {};
  if (plan?.name && typeof plan.name === "object") {
    Object.entries(plan.name).forEach(([lang, value]) => {
      const langKey = normalizeString(lang).trim();
      if (!langKey) return;
      name[langKey.toLowerCase()] = value?.toString() ?? "";
    });
  }

  const productIds = Array.isArray(plan?.productIds)
    ? plan.productIds
      .map((id) => normalizeString(id).trim())
      .filter(Boolean)
    : [];

  const features = Array.isArray(plan?.features)
    ? plan.features.map(normalizePlanFeature)
    : [];

  const defaultOffers = Array.isArray(plan?.defaultOffers)
    ? plan.defaultOffers.map(normalizePlanOffer)
    : [];

  return {
    id: normalizeString(plan?.id || "").trim(),
    name,
    productIds,
    isActive: plan?.isActive === true,
    order: normalizeNumber(plan?.order) ?? 0,
    matchingVisibilityScore:
      normalizeNumber(plan?.matchingVisibilityScore) ?? 0,
    features,
    defaultOffers,
    createdAt: plan?.createdAt,
    updatedAt: plan?.updatedAt,
  };
};

const applyPlanDraft = (plan) => {
  const normalized = normalizePlan(plan || {});
  planDraft.id = normalized.id;
  planDraft.name = { ...normalized.name };
  planDraft.productIds = normalized.productIds.length
    ? [...normalized.productIds]
    : [""];
  planDraft.isActive = normalized.isActive;
  planDraft.order = normalized.order;
  planDraft.matchingVisibilityScore = normalized.matchingVisibilityScore;
};

const resetPlanDraft = () => {
  planDraft.id = "";
  planDraft.name = {};
  planDraft.productIds = [""];
  planDraft.isActive = false;
  planDraft.order = 5;
  planDraft.matchingVisibilityScore = 500;
};

const startCreatePlan = () => {
  editingPlanId.value = null;
  planFormMessage.value = "";
  planFormMessageType.value = "success";
  resetPlanDraft();
  isPlanModalOpen.value = true;
};

const startEditPlan = (plan) => {
  if (!plan?.id) return;
  editingPlanId.value = plan.id;
  planFormMessage.value = "";
  planFormMessageType.value = "success";
  applyPlanDraft(plan);
  isPlanModalOpen.value = true;
};

const addProductRow = () => {
  planDraft.productIds = [...planDraft.productIds, ""];
};

const removeProductRow = (index) => {
  if (planDraft.productIds.length <= 1) {
    planDraft.productIds = [""];
    return;
  }
  planDraft.productIds = planDraft.productIds.filter((_, idx) => idx !== index);
};

const addFeatureRow = (featureKey) => {
  const key = normalizeString(featureKey || "").trim();
  if (!key) return;
  if (featuresDraft.features.some((feature) => feature.payableFeature === key)) {
    selectedFeatureToAdd.value = "";
    return;
  }
  featuresDraft.features = [
    ...featuresDraft.features,
    { payableFeature: key, windowLimit: 0, windowDuration: "" },
  ];
  selectedFeatureToAdd.value = "";
};

const removeFeatureRow = (index) => {
  featuresDraft.features = featuresDraft.features.filter((_, idx) => idx !== index);
};

const applyFeaturesDraft = (plan) => {
  const normalized = normalizePlan(plan || {});
  featuresDraft.features = normalized.features.map((feature) => ({
    payableFeature: feature.payableFeature,
    windowLimit: feature.windowLimit,
    windowDuration: feature.windowDuration,
  }));
  selectedFeatureToAdd.value = "";
};

const startEditFeatures = (plan) => {
  if (!plan?.id) return;
  editingFeaturesPlanId.value = plan.id;
  featuresFormMessage.value = "";
  featuresFormMessageType.value = "success";
  applyFeaturesDraft(plan);
  isFeaturesModalOpen.value = true;
};

const closeFeaturesModal = () => {
  isFeaturesModalOpen.value = false;
};

const resetFeaturesForm = () => {
  featuresFormMessage.value = "";
  featuresFormMessageType.value = "success";
  const existingPlan = plans.value.find(
    (plan) => plan.id === editingFeaturesPlanId.value
  );
  if (existingPlan) {
    applyFeaturesDraft(existingPlan);
  }
};

const buildFeaturesPayload = () => {
  const features = (featuresDraft.features || [])
    .filter((feature) => feature.payableFeature)
    .map((feature) => ({
      payableFeature: feature.payableFeature,
      windowLimit: normalizeNumber(feature.windowLimit) ?? 0,
      windowDuration:
        normalizeString(feature.windowDuration || "").trim() || null,
    }));

  return { features };
};

const saveFeatures = async () => {
  const targetId = normalizeString(editingFeaturesPlanId.value || "").trim();
  featuresFormMessage.value = "";
  featuresFormMessageType.value = "success";
  if (!targetId) {
    featuresFormMessage.value = "Plan id is required.";
    featuresFormMessageType.value = "danger";
    return;
  }

  featuresSaving.value = true;
  try {
    const payload = buildFeaturesPayload();
    await upsertPlan(targetId, payload);
    await loadPlans(true);
    featuresFormMessage.value = "Features saved.";
    featuresFormMessageType.value = "success";
    plansStatus.value = "";
  } catch (error) {
    console.error("Failed to save features", error);
    featuresFormMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Unable to save features. Please try again.";
    featuresFormMessageType.value = "danger";
  } finally {
    featuresSaving.value = false;
  }
};

const applyOffersDraft = (plan) => {
  const normalized = normalizePlan(plan || {});
  offersDraft.offers = normalized.defaultOffers.map((offer) => ({ ...offer }));
};

const startEditOffers = (plan) => {
  if (!plan?.id) return;
  editingOffersPlanId.value = plan.id;
  offersFormMessage.value = "";
  offersFormMessageType.value = "success";
  applyOffersDraft(plan);
  isOffersModalOpen.value = true;
};

const closeOffersModal = () => {
  isOffersModalOpen.value = false;
};

const resetOffersForm = () => {
  offersFormMessage.value = "";
  offersFormMessageType.value = "success";
  const existingPlan = plans.value.find(
    (plan) => plan.id === editingOffersPlanId.value
  );
  if (existingPlan) {
    applyOffersDraft(existingPlan);
  }
};

const addOfferRow = () => {
  offersDraft.offers = [
    ...offersDraft.offers,
    {
      id: "",
      title: "",
      description: "",
      prices: [],
      subscriptionPeriod: "",
    },
  ];
};

const removeOfferRow = (index) => {
  offersDraft.offers = offersDraft.offers.filter((_, idx) => idx !== index);
};

const addOfferPriceRow = (offerIndex) => {
  const offer = offersDraft.offers[offerIndex];
  if (!offer) return;
  offer.prices = [...(offer.prices || []), { currencyCode: "USD", price: 0 }];
};

const removeOfferPriceRow = (offerIndex, priceIndex) => {
  const offer = offersDraft.offers[offerIndex];
  if (!offer) return;
  offer.prices = offer.prices.filter((_, idx) => idx !== priceIndex);
};

const buildOffersPayload = () => {
  const defaultOffers = (offersDraft.offers || [])
    .filter((offer) => normalizeString(offer.id || "").trim())
    .map((offer) => ({
      id: normalizeString(offer.id).trim(),
      title: normalizeString(offer.title || ""),
      description: normalizeString(offer.description || ""),
      prices: (offer.prices || [])
        .filter((price) => normalizeString(price.currencyCode || "").trim())
        .map((price) => ({
          currencyCode: normalizeString(price.currencyCode).trim(),
          price: normalizeNumber(price.price) ?? 0,
        })),
      subscriptionPeriod:
        normalizeString(offer.subscriptionPeriod || "").trim() || null,
    }));

  return { defaultOffers };
};

const saveOffers = async () => {
  const targetId = normalizeString(editingOffersPlanId.value || "").trim();
  offersFormMessage.value = "";
  offersFormMessageType.value = "success";
  if (!targetId) {
    offersFormMessage.value = "Plan id is required.";
    offersFormMessageType.value = "danger";
    return;
  }

  const hasInvalidOffer = offersDraft.offers.some((offer) => {
    if (!normalizeString(offer.id || "").trim()) return true;
    const prices = offer.prices || [];
    if (prices.length === 0) return true;
    const currencies = new Set();
    return prices.some((price) => {
      const currencyCode = normalizeString(price.currencyCode || "").trim();
      if (!currencyCode) return true;
      if (currencies.has(currencyCode)) return true;
      currencies.add(currencyCode);
      return !(normalizeNumber(price.price) > 0);
    });
  });
  if (hasInvalidOffer) {
    offersFormMessage.value =
      "Each offer needs an id and at least one price with a unique currency and a positive amount.";
    offersFormMessageType.value = "danger";
    return;
  }

  offersSaving.value = true;
  try {
    const payload = buildOffersPayload();
    await upsertPlan(targetId, payload);
    await loadPlans(true);
    offersFormMessage.value = "Offers saved.";
    offersFormMessageType.value = "success";
    plansStatus.value = "";
  } catch (error) {
    console.error("Failed to save offers", error);
    offersFormMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Unable to save offers. Please try again.";
    offersFormMessageType.value = "danger";
  } finally {
    offersSaving.value = false;
  }
};

const resetPlanForm = () => {
  planFormMessage.value = "";
  planFormMessageType.value = "success";
  if (isEditingPlan.value) {
    const existingPlan = plans.value.find(
      (plan) => plan.id === editingPlanId.value
    );
    if (existingPlan) {
      applyPlanDraft(existingPlan);
      return;
    }
  }
  resetPlanDraft();
};

const closePlanModal = () => {
  isPlanModalOpen.value = false;
};

const buildPlanPayload = () => {
  const productIds = (planDraft.productIds || [])
    .map((id) => normalizeString(id).trim())
    .filter(Boolean);

  const name = {};
  Object.entries(planDraft.name || {}).forEach(([lang, value]) => {
    const langKey = normalizeString(lang).trim();
    if (!langKey) return;
    name[langKey.toLowerCase()] = value?.toString() ?? "";
  });

  return {
    name,
    productIds,
    isActive: Boolean(planDraft.isActive),
    order: normalizeNumber(planDraft.order) ?? 0,
    matchingVisibilityScore:
      normalizeNumber(planDraft.matchingVisibilityScore) ?? 0,
  };
};

const savePlan = async () => {
  const targetId = normalizeString(
    isEditingPlan.value ? editingPlanId.value : planDraft.id
  ).trim();
  planFormMessage.value = "";
  planFormMessageType.value = "success";
  if (!targetId) {
    planFormMessage.value = "Plan id is required.";
    planFormMessageType.value = "danger";
    return;
  }

  planSaving.value = true;
  try {
    const payload = buildPlanPayload();
    await upsertPlan(targetId, payload);
    await loadPlans(true);
    planFormMessage.value = "Plan saved.";
    planFormMessageType.value = "success";
    plansStatus.value = "";
  } catch (error) {
    console.error("Failed to save plan", error);
    planFormMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Unable to save plan. Please try again.";
    planFormMessageType.value = "danger";
  } finally {
    planSaving.value = false;
  }
};

const loadPlans = async (force = false) => {
  if (plansLoading.value || (plansLoaded.value && !force)) return;
  plansError.value = "";
  plansStatus.value = "";
  plansStatusType.value = "success";
  plansLoading.value = true;
  try {
    const payload = await fetchPlans();
    const items = Array.isArray(payload) ? payload : [];
    plans.value = items
      .map(normalizePlan)
      .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
    plansLoaded.value = true;
    if (isEditingPlan.value) {
      const existing = plans.value.find(
        (plan) => plan.id === editingPlanId.value
      );
      if (existing) {
        applyPlanDraft(existing);
      }
    }
    if (editingOffersPlanId.value) {
      const existingForOffers = plans.value.find(
        (plan) => plan.id === editingOffersPlanId.value
      );
      if (existingForOffers) {
        applyOffersDraft(existingForOffers);
      }
    }
    if (editingFeaturesPlanId.value) {
      const existingForFeatures = plans.value.find(
        (plan) => plan.id === editingFeaturesPlanId.value
      );
      if (existingForFeatures) {
        applyFeaturesDraft(existingForFeatures);
      }
    }
    if (force) {
      plansStatus.value = "Plans refreshed.";
      plansStatusType.value = "success";
    }
  } catch (error) {
    console.error("Failed to load plans", error);
    plansError.value =
      error?.payload?.message ||
      error?.message ||
      "Unable to load plans. Please try again.";
  } finally {
    plansLoading.value = false;
  }
};

onMounted(loadConfig);
watch(
  () => [selectedTemplateName.value, selectedTemplateLanguage.value],
  ([name, lang], [oldName, oldLang]) => {
    if (!name || !lang) return;
    if (name !== oldName || lang !== oldLang) {
      applyTemplateFromList();
    }
  }
);
watch(
  () => route.query.tab,
  () => {
    applyTabFromRoute();
  }
);

applyTabFromRoute();

watch(
  () => activeTab.value,
  (value) => {
    if (value === "emailTemplates") {
      loadEmailTemplates();
    }
    if (value === "profileAttributes") {
      loadProfileAttributes();
    }
    if (value === "plans") {
      loadPlans();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="py-4 container-fluid">
    <div v-if="isLoading" class="card">
      <div class="card-body text-center py-5">
        <div class="spinner-border text-success" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3 mb-0 text-sm text-secondary">Loading configuration...</p>
      </div>
    </div>

    <div v-else-if="errorMessage" class="card">
      <div class="card-body text-center py-5">
        <div class="icon icon-shape bg-gradient-danger text-white shadow text-center border-radius-md mx-auto">
          <i class="ni ni-fat-remove"></i>
        </div>
        <p class="mt-3 text-sm">{{ errorMessage }}</p>
        <button class="btn btn-sm btn-success" type="button" @click="loadConfig">
          Try again
        </button>
      </div>
    </div>

    <div v-else class="card">
      <div class="d-flex">
        <div class="border-end py-3 px-2" style="min-width: 200px; max-width: 200px;">
          <ul class="nav nav-pills flex-column gap-1">
            <li v-for="tab in TAB_ITEMS" :key="tab.key" class="nav-item">
              <button type="button" class="nav-link mb-0 w-100 text-start" :class="{ 'active': activeTab === tab.key }"
                @click="selectTab(tab.key)">
                {{ tab.label }}
              </button>
            </li>
          </ul>
        </div>
        <div class="flex-grow-1">
          <argon-alert v-if="statusMessage && !isLoading" class="mx-3 mt-3">
            {{ statusMessage }}
          </argon-alert>
          <div class="card-body">
            <fieldset :disabled="!canEditConfig" class="border-0 p-0 m-0">
              <!-- Brand -->
              <div v-if="activeTab === 'brand'">
                <div class="row g-3">
                  <div class="col-12 col-lg-6">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <div class="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h6 class="mb-1">Brand</h6>
                            <p class="text-xs text-secondary mb-0">
                              Manage your application name, logo and languages.
                            </p>
                          </div>
                        </div>

                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Application name</label>
                          <input class="form-control" type="text" v-model="draftValues.appName"
                            placeholder="MegaMatch" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('appName') || isSavingKey('appName')"
                              @click="handleSave('appName')">
                              <span v-if="isSavingKey('appName')" class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('appName') || isSavingKey('appName')"
                              @click="handleReset('appName')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Logo URL</label>
                          <input class="form-control" type="text" v-model="draftValues.logoUrl"
                            placeholder="https://" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('logoUrl') || isSavingKey('logoUrl')"
                              @click="handleSave('logoUrl')">
                              <span v-if="isSavingKey('logoUrl')" class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('logoUrl') || isSavingKey('logoUrl')"
                              @click="handleReset('logoUrl')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Languages</label>
                          <div class="d-flex flex-wrap mb-2" style="gap: 8px">
                            <span v-for="lang in normalizeStringList(draftValues.languages || [])" :key="lang"
                              class="badge bg-light text-dark border">
                              {{ lang }}
                              <a href="javascript:;" class="ms-2 text-danger" @click.prevent="removeLanguage(lang)">
                                &times;
                              </a>
                            </span>
                            <span v-if="!draftValues.languages || draftValues.languages.length === 0"
                              class="text-xs text-secondary">
                              No languages added yet.
                            </span>
                          </div>
                          <div class="input-group input-group-sm mb-2">
                            <select class="form-select" v-model="newLanguageInput">
                              <option value="">Select language</option>
                              <option
                                v-for="lang in languageOptions.filter((code) => !normalizeStringList(draftValues.languages || []).includes(code))"
                                :key="lang" :value="lang">
                                {{ lang }}
                              </option>
                            </select>
                            <button class="btn btn-outline-secondary mb-0" type="button" :disabled="!newLanguageInput"
                              @click="addLanguage(newLanguageInput)">
                              Add
                            </button>
                          </div>
                          <small class="text-xs text-secondary">Need a new code? Type it above and click Add.</small>
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('languages') || isSavingKey('languages')"
                              @click="handleSave('languages')">
                              <span v-if="isSavingKey('languages')" class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('languages') || isSavingKey('languages')"
                              @click="handleReset('languages')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <div>
                          <label class="form-label text-sm fw-semibold">Default language</label>
                          <input class="form-control" type="text" v-model="draftValues.defaultLanguage"
                            placeholder="EN" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('defaultLanguage') || isSavingKey('defaultLanguage')"
                              @click="handleSave('defaultLanguage')">
                              <span v-if="isSavingKey('defaultLanguage')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('defaultLanguage') || isSavingKey('defaultLanguage')"
                              @click="handleReset('defaultLanguage')">
                              Reset
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Admin Notification -->
              <div v-else-if="activeTab === 'adminNotification'">
                <div class="row g-3">
                  <div class="col-12 col-lg-4">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">Email</h6>
                        <p class="text-xs text-secondary mb-3">Where admin notifications are sent.</p>
                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Notification email</label>
                          <input class="form-control" type="email" v-model="draftValues.notifyAdminEmail"
                            placeholder="ops@megamatch.ai" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('notifyAdminEmail') || isSavingKey('notifyAdminEmail')"
                              @click="handleSave('notifyAdminEmail')">
                              <span v-if="isSavingKey('notifyAdminEmail')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('notifyAdminEmail') || isSavingKey('notifyAdminEmail')"
                              @click="handleReset('notifyAdminEmail')">
                              Reset
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-lg-4">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">Telegram</h6>
                        <p class="text-xs text-secondary mb-3">Bot credentials for admin alerts.</p>

                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Bot API key</label>
                          <input class="form-control" type="text" v-model="draftValues.notifyAdminTelegramBotApiKey"
                            placeholder="123456:ABC" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('notifyAdminTelegramBotApiKey') || isSavingKey('notifyAdminTelegramBotApiKey')"
                              @click="handleSave('notifyAdminTelegramBotApiKey')">
                              <span v-if="isSavingKey('notifyAdminTelegramBotApiKey')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('notifyAdminTelegramBotApiKey') || isSavingKey('notifyAdminTelegramBotApiKey')"
                              @click="handleReset('notifyAdminTelegramBotApiKey')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <div>
                          <label class="form-label text-sm fw-semibold">Chat ID</label>
                          <input class="form-control" type="text" v-model="draftValues.notifyAdminTelegramChatId"
                            placeholder="123456789" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('notifyAdminTelegramChatId') || isSavingKey('notifyAdminTelegramChatId')"
                              @click="handleSave('notifyAdminTelegramChatId')">
                              <span v-if="isSavingKey('notifyAdminTelegramChatId')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('notifyAdminTelegramChatId') || isSavingKey('notifyAdminTelegramChatId')"
                              @click="handleReset('notifyAdminTelegramChatId')">
                              Reset
                            </button>
                          </div>
                          <p class="text-xs text-secondary mb-0 mt-2">Used for any event with Telegram enabled that
                            has no per-event override below.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-lg-4">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">Slack</h6>
                        <p class="text-xs text-secondary mb-3">Default incoming webhook for admin alerts.</p>
                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Webhook URL</label>
                          <input class="form-control" type="text" v-model="draftValues.notifyAdminSlackWebhookUrl"
                            placeholder="https://hooks.slack.com/services/..." />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('notifyAdminSlackWebhookUrl') || isSavingKey('notifyAdminSlackWebhookUrl')"
                              @click="handleSave('notifyAdminSlackWebhookUrl')">
                              <span v-if="isSavingKey('notifyAdminSlackWebhookUrl')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('notifyAdminSlackWebhookUrl') || isSavingKey('notifyAdminSlackWebhookUrl')"
                              @click="handleReset('notifyAdminSlackWebhookUrl')">
                              Reset
                            </button>
                          </div>
                        </div>
                        <p class="text-xs text-secondary mb-0">Used for any event with Slack enabled that has no
                          per-event override below.</p>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-lg-4">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">Google Chat</h6>
                        <p class="text-xs text-secondary mb-3">Default incoming webhook for admin alerts.</p>
                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Webhook URL</label>
                          <input class="form-control" type="text"
                            v-model="draftValues.notifyAdminGoogleChatWebhookUrl"
                            placeholder="https://chat.googleapis.com/v1/spaces/.../messages?key=...&token=..." />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('notifyAdminGoogleChatWebhookUrl') || isSavingKey('notifyAdminGoogleChatWebhookUrl')"
                              @click="handleSave('notifyAdminGoogleChatWebhookUrl')">
                              <span v-if="isSavingKey('notifyAdminGoogleChatWebhookUrl')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('notifyAdminGoogleChatWebhookUrl') || isSavingKey('notifyAdminGoogleChatWebhookUrl')"
                              @click="handleReset('notifyAdminGoogleChatWebhookUrl')">
                              Reset
                            </button>
                          </div>
                        </div>
                        <p class="text-xs text-secondary mb-0">Used for any event with Google Chat enabled that has
                          no per-event override below.</p>
                      </div>
                    </div>
                  </div>

                  <div class="col-12">
                    <div class="card border shadow-sm">
                      <div class="card-body">
                        <div class="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h6 class="mb-1">Channels matrix</h6>
                            <p class="text-xs text-secondary mb-0">Enable channels per admin notification event.</p>
                          </div>
                        </div>

                        <div class="table-responsive mb-3">
                          <table class="table align-items-center mb-0">
                            <thead>
                              <tr>
                                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Event
                                </th>
                                <th v-for="channel in notifyChannels" :key="channel"
                                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                                  {{ channel }}
                                </th>
                                <th class="text-secondary opacity-7"></th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="event in notifyEvents" :key="event">
                                <td class="text-sm font-weight-bold">{{ event }}</td>
                                <td v-for="channel in notifyChannels" :key="`${event}-${channel}`"
                                  :id="`notify-${event}-${channel}`" class="text-center">
                                  <div class="form-check form-switch d-flex justify-content-center">
                                    <input class="form-check-input" type="checkbox"
                                      :checked="(draftValues.notifyAdminChannels?.[event] || []).includes(channel)"
                                      @change="toggleNotifyChannel(event, channel, $event.target.checked)" />
                                  </div>
                                  <input
                                    v-if="channel === 'slack' && (draftValues.notifyAdminChannels?.[event] || []).includes('slack')"
                                    class="form-control form-control-sm mt-1" type="text"
                                    placeholder="Webhook URL override (optional)"
                                    :value="draftValues.notifyAdminSlackWebhookUrlsByEvent?.[event] || ''"
                                    @input="setSlackWebhookOverride(event, $event.target.value)" />
                                  <input
                                    v-if="channel === 'telegram' && (draftValues.notifyAdminChannels?.[event] || []).includes('telegram')"
                                    class="form-control form-control-sm mt-1" type="text"
                                    placeholder="Chat ID override (optional)"
                                    :value="draftValues.notifyAdminTelegramChatIdsByEvent?.[event] || ''"
                                    @input="setTelegramChatIdOverride(event, $event.target.value)" />
                                  <input
                                    v-if="channel === 'googlechat' && (draftValues.notifyAdminChannels?.[event] || []).includes('googlechat')"
                                    class="form-control form-control-sm mt-1" type="text"
                                    placeholder="Webhook URL override (optional)"
                                    :value="draftValues.notifyAdminGoogleChatWebhookUrlsByEvent?.[event] || ''"
                                    @input="setGoogleChatWebhookOverride(event, $event.target.value)" />
                                </td>
                                <td class="text-end">
                                  <button class="btn btn-link text-danger text-xs p-0" type="button" @click="() => {
                                    const map = { ...(draftValues.notifyAdminChannels || {}) };
                                    delete map[event];
                                    assignValue('notifyAdminChannels', map);
                                  }">
                                    Remove
                                  </button>
                                </td>
                              </tr>
                              <tr v-if="notifyEvents.length === 0">
                                <td colspan="99" class="text-center text-sm text-secondary py-3">
                                  No events configured. Add one below to begin.
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        <div class="row g-2 align-items-center">
                          <div class="col-sm-6">
                            <div class="input-group input-group-sm">
                              <span class="input-group-text bg-white">
                                <i class="ni ni-bell-55"></i>
                              </span>
                              <input class="form-control" type="text" placeholder="Event name (e.g. signup)"
                                v-model="notifyEventInput" />
                            </div>
                          </div>
                          <div class="col-auto">
                            <button class="btn btn-sm btn-outline-secondary mb-0" type="button" @click="addNotifyEvent">
                              Add event
                            </button>
                          </div>
                        </div>

                        <p class="text-xs text-secondary mb-2">A Slack override on a row sends that event to its own
                          channel/webhook instead of the default Slack webhook above. A Telegram override sends it to
                          its own chat instead of the default Telegram chat ID above. A Google Chat override sends it
                          to its own space/webhook instead of the default Google Chat webhook above.</p>

                        <div class="mt-3 d-flex gap-2 align-items-center flex-wrap">
                          <button class="btn btn-sm btn-success"
                            :disabled="!hasChanges('notifyAdminChannels') || isSavingKey('notifyAdminChannels')"
                            @click="handleSave('notifyAdminChannels')">
                            <span v-if="isSavingKey('notifyAdminChannels')"
                              class="spinner-border spinner-border-sm"></span>
                            <span v-else>Save channels</span>
                          </button>
                          <button class="btn btn-sm btn-outline-secondary"
                            :disabled="!hasChanges('notifyAdminChannels') || isSavingKey('notifyAdminChannels')"
                            @click="handleReset('notifyAdminChannels')">
                            Reset channels
                          </button>
                          <button class="btn btn-sm btn-success"
                            :disabled="!hasChanges('notifyAdminSlackWebhookUrlsByEvent') || isSavingKey('notifyAdminSlackWebhookUrlsByEvent')"
                            @click="handleSave('notifyAdminSlackWebhookUrlsByEvent')">
                            <span v-if="isSavingKey('notifyAdminSlackWebhookUrlsByEvent')"
                              class="spinner-border spinner-border-sm"></span>
                            <span v-else>Save Slack overrides</span>
                          </button>
                          <button class="btn btn-sm btn-outline-secondary"
                            :disabled="!hasChanges('notifyAdminSlackWebhookUrlsByEvent') || isSavingKey('notifyAdminSlackWebhookUrlsByEvent')"
                            @click="handleReset('notifyAdminSlackWebhookUrlsByEvent')">
                            Reset overrides
                          </button>
                          <button class="btn btn-sm btn-success"
                            :disabled="!hasChanges('notifyAdminTelegramChatIdsByEvent') || isSavingKey('notifyAdminTelegramChatIdsByEvent')"
                            @click="handleSave('notifyAdminTelegramChatIdsByEvent')">
                            <span v-if="isSavingKey('notifyAdminTelegramChatIdsByEvent')"
                              class="spinner-border spinner-border-sm"></span>
                            <span v-else>Save Telegram overrides</span>
                          </button>
                          <button class="btn btn-sm btn-outline-secondary"
                            :disabled="!hasChanges('notifyAdminTelegramChatIdsByEvent') || isSavingKey('notifyAdminTelegramChatIdsByEvent')"
                            @click="handleReset('notifyAdminTelegramChatIdsByEvent')">
                            Reset overrides
                          </button>
                          <button class="btn btn-sm btn-success"
                            :disabled="!hasChanges('notifyAdminGoogleChatWebhookUrlsByEvent') || isSavingKey('notifyAdminGoogleChatWebhookUrlsByEvent')"
                            @click="handleSave('notifyAdminGoogleChatWebhookUrlsByEvent')">
                            <span v-if="isSavingKey('notifyAdminGoogleChatWebhookUrlsByEvent')"
                              class="spinner-border spinner-border-sm"></span>
                            <span v-else>Save Google Chat overrides</span>
                          </button>
                          <button class="btn btn-sm btn-outline-secondary"
                            :disabled="!hasChanges('notifyAdminGoogleChatWebhookUrlsByEvent') || isSavingKey('notifyAdminGoogleChatWebhookUrlsByEvent')"
                            @click="handleReset('notifyAdminGoogleChatWebhookUrlsByEvent')">
                            Reset overrides
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Technical -->
              <div v-else-if="activeTab === 'technical'">
                <div class="row g-3">
                  <div class="col-12 col-lg-6">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">SMS</h6>
                        <p class="text-xs text-secondary mb-3">Sender identity for outbound SMS.</p>
                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Sender ID</label>
                          <input class="form-control" type="text" v-model="draftValues.smsFrom"
                            placeholder="MegaMatch" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('smsFrom') || isSavingKey('smsFrom')"
                              @click="handleSave('smsFrom')">
                              <span v-if="isSavingKey('smsFrom')" class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('smsFrom') || isSavingKey('smsFrom')"
                              @click="handleReset('smsFrom')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <h6 class="mt-4 mb-1">IranPayamak</h6>
                        <p class="text-xs text-secondary mb-3">OTP delivery for Iranian numbers, tried first.</p>
                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">API key</label>
                          <input class="form-control" type="text" v-model="draftValues.iranPayamakApiKey"
                            placeholder="Api-Key" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('iranPayamakApiKey') || isSavingKey('iranPayamakApiKey')"
                              @click="handleSave('iranPayamakApiKey')">
                              <span v-if="isSavingKey('iranPayamakApiKey')" class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('iranPayamakApiKey') || isSavingKey('iranPayamakApiKey')"
                              @click="handleReset('iranPayamakApiKey')">
                              Reset
                            </button>
                          </div>
                        </div>
                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Template code</label>
                          <input class="form-control" type="text" v-model="draftValues.iranPayamakTemplateCode"
                            placeholder="Pattern code" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('iranPayamakTemplateCode') || isSavingKey('iranPayamakTemplateCode')"
                              @click="handleSave('iranPayamakTemplateCode')">
                              <span v-if="isSavingKey('iranPayamakTemplateCode')" class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('iranPayamakTemplateCode') || isSavingKey('iranPayamakTemplateCode')"
                              @click="handleReset('iranPayamakTemplateCode')">
                              Reset
                            </button>
                          </div>
                        </div>
                        <div>
                          <label class="form-label text-sm fw-semibold">Code parameter name</label>
                          <input class="form-control" type="text" v-model="draftValues.iranPayamakCodeParameterName"
                            placeholder="code" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('iranPayamakCodeParameterName') || isSavingKey('iranPayamakCodeParameterName')"
                              @click="handleSave('iranPayamakCodeParameterName')">
                              <span v-if="isSavingKey('iranPayamakCodeParameterName')" class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('iranPayamakCodeParameterName') || isSavingKey('iranPayamakCodeParameterName')"
                              @click="handleReset('iranPayamakCodeParameterName')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <h6 class="mt-4 mb-1">SMS.ir</h6>
                        <p class="text-xs text-secondary mb-3">OTP delivery for Iranian numbers, tried after IranPayamak.</p>
                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">API key</label>
                          <input class="form-control" type="text" v-model="draftValues.smsIrApiKey"
                            placeholder="x-api-key" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('smsIrApiKey') || isSavingKey('smsIrApiKey')"
                              @click="handleSave('smsIrApiKey')">
                              <span v-if="isSavingKey('smsIrApiKey')" class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('smsIrApiKey') || isSavingKey('smsIrApiKey')"
                              @click="handleReset('smsIrApiKey')">
                              Reset
                            </button>
                          </div>
                        </div>
                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Template ID</label>
                          <input class="form-control" type="text" v-model="draftValues.smsIrTemplateId"
                            placeholder="390921" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('smsIrTemplateId') || isSavingKey('smsIrTemplateId')"
                              @click="handleSave('smsIrTemplateId')">
                              <span v-if="isSavingKey('smsIrTemplateId')" class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('smsIrTemplateId') || isSavingKey('smsIrTemplateId')"
                              @click="handleReset('smsIrTemplateId')">
                              Reset
                            </button>
                          </div>
                        </div>
                        <div>
                          <label class="form-label text-sm fw-semibold">Code parameter name</label>
                          <input class="form-control" type="text" v-model="draftValues.smsIrCodeParameterName"
                            placeholder="CODE" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('smsIrCodeParameterName') || isSavingKey('smsIrCodeParameterName')"
                              @click="handleSave('smsIrCodeParameterName')">
                              <span v-if="isSavingKey('smsIrCodeParameterName')" class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('smsIrCodeParameterName') || isSavingKey('smsIrCodeParameterName')"
                              @click="handleReset('smsIrCodeParameterName')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <div class="mt-4">
                          <label class="form-label text-sm fw-semibold">Additional template parameters</label>
                          <p class="text-xs text-secondary mb-2">
                            Extra name/value pairs sent alongside the code parameter in the sms.ir template request.
                          </p>
                          <div v-if="(draftValues.smsIrAdditionalParameters || []).length"
                            class="d-flex flex-column gap-2 mb-3">
                            <div v-for="param in draftValues.smsIrAdditionalParameters" :key="param.name"
                              class="input-group input-group-sm">
                              <span class="input-group-text text-xs" style="min-width:160px">{{ param.name }}</span>
                              <input class="form-control" type="text" placeholder="Value" :value="param.value"
                                @input="updateSmsIrParameterValue(param.name, $event.target.value)" />
                              <button class="btn btn-outline-danger mb-0" type="button"
                                @click="removeSmsIrParameter(param.name)">
                                &times;
                              </button>
                            </div>
                          </div>
                          <p v-else class="text-xs text-secondary mb-3">No additional parameters added yet.</p>

                          <div class="input-group input-group-sm mb-3">
                            <input class="form-control" type="text" v-model="newSmsIrParamName"
                              placeholder="Parameter name" @keyup.enter="addSmsIrParameter" />
                            <input class="form-control" type="text" v-model="newSmsIrParamValue" placeholder="Value"
                              @keyup.enter="addSmsIrParameter" />
                            <button class="btn btn-outline-secondary mb-0" type="button"
                              :disabled="!newSmsIrParamName" @click="addSmsIrParameter">
                              Add
                            </button>
                          </div>

                          <div class="d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('smsIrAdditionalParameters') || isSavingKey('smsIrAdditionalParameters')"
                              @click="handleSave('smsIrAdditionalParameters')">
                              <span v-if="isSavingKey('smsIrAdditionalParameters')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('smsIrAdditionalParameters') || isSavingKey('smsIrAdditionalParameters')"
                              @click="handleReset('smsIrAdditionalParameters')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <h6 class="mt-4 mb-1">Email</h6>
                        <p class="text-xs text-secondary mb-3">From address and display name.</p>

                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">From address</label>
                          <input class="form-control" type="text" v-model="draftValues.emailFrom"
                            placeholder="no-reply@megamatch.ai" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('emailFrom') || isSavingKey('emailFrom')"
                              @click="handleSave('emailFrom')">
                              <span v-if="isSavingKey('emailFrom')" class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('emailFrom') || isSavingKey('emailFrom')"
                              @click="handleReset('emailFrom')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <div>
                          <label class="form-label text-sm fw-semibold">Display name</label>
                          <input class="form-control" type="text" v-model="draftValues.emailFromName"
                            placeholder="MegaMatch" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('emailFromName') || isSavingKey('emailFromName')"
                              @click="handleSave('emailFromName')">
                              <span v-if="isSavingKey('emailFromName')" class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('emailFromName') || isSavingKey('emailFromName')"
                              @click="handleReset('emailFromName')">
                              Reset
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-lg-6">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">Geo service</h6>
                        <p class="text-xs text-secondary mb-3">Mapbox configuration.</p>
                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Mapbox token</label>
                          <input class="form-control" type="text" v-model="draftValues.mapBoxToken"
                            placeholder="pk..." />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('mapBoxToken') || isSavingKey('mapBoxToken')"
                              @click="handleSave('mapBoxToken')">
                              <span v-if="isSavingKey('mapBoxToken')" class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('mapBoxToken') || isSavingKey('mapBoxToken')"
                              @click="handleReset('mapBoxToken')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <div class="form-check form-switch mb-3">
                          <input class="form-check-input" type="checkbox" id="useOpenSearchForGeocoding"
                            :checked="draftValues.useOpenSearchForGeocoding === true"
                            @change="assignValue('useOpenSearchForGeocoding', $event.target.checked)" />
                          <label class="form-check-label text-sm fw-semibold me-2" for="useOpenSearchForGeocoding">
                            Use OpenSearch for geocoding
                          </label>
                          <p class="text-xs text-secondary mt-1 mb-0">
                            Off: Mapbox (default). On: OpenSearch (geonames-backed, requires the geo_places index to be
                            built).
                          </p>
                        </div>

                        <div class="d-flex gap-2 align-items-center">
                          <button class="btn btn-sm btn-success"
                            :disabled="!hasChanges('useOpenSearchForGeocoding') || isSavingKey('useOpenSearchForGeocoding')"
                            @click="handleSave('useOpenSearchForGeocoding')">
                            <span v-if="isSavingKey('useOpenSearchForGeocoding')"
                              class="spinner-border spinner-border-sm"></span>
                            <span v-else>Save</span>
                          </button>
                          <button class="btn btn-sm btn-outline-secondary"
                            :disabled="!hasChanges('useOpenSearchForGeocoding') || isSavingKey('useOpenSearchForGeocoding')"
                            @click="handleReset('useOpenSearchForGeocoding')">
                            Reset
                          </button>
                        </div>

                        <div class="mt-3">
                          <label class="form-label text-sm fw-semibold">OpenSearch geocoder min population</label>
                          <input class="form-control" type="number" min="0"
                            :value="draftValues.geocoderMinPopulation ?? ''"
                            @input="numericInputHandler('geocoderMinPopulation', $event.target.value)" />
                          <p class="text-xs text-secondary mt-1 mb-0">
                            Locations below this population are hidden from OpenSearch geocoding results. Default
                            10000; 0 disables the filter.
                          </p>
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('geocoderMinPopulation') || isSavingKey('geocoderMinPopulation')"
                              @click="handleSave('geocoderMinPopulation')">
                              <span v-if="isSavingKey('geocoderMinPopulation')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('geocoderMinPopulation') || isSavingKey('geocoderMinPopulation')"
                              @click="handleReset('geocoderMinPopulation')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <h6 class="mt-4 mb-1">OpenAI</h6>
                        <p class="text-xs text-secondary mb-3">API credentials.</p>
                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">OpenAI API key</label>
                          <input class="form-control" type="text" v-model="draftValues.openAiApiKey"
                            placeholder="sk-" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('openAiApiKey') || isSavingKey('openAiApiKey')"
                              @click="handleSave('openAiApiKey')">
                              <span v-if="isSavingKey('openAiApiKey')" class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('openAiApiKey') || isSavingKey('openAiApiKey')"
                              @click="handleReset('openAiApiKey')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <h6 class="mt-4 mb-1">Telegram sign in</h6>
                        <p class="text-xs text-secondary mb-3">Bot settings for sign-in.</p>
                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Bot API key</label>
                          <input class="form-control" type="text" v-model="draftValues.telegramSignInBotApiKey"
                            placeholder="123456:ABC" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('telegramSignInBotApiKey') || isSavingKey('telegramSignInBotApiKey')"
                              @click="handleSave('telegramSignInBotApiKey')">
                              <span v-if="isSavingKey('telegramSignInBotApiKey')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('telegramSignInBotApiKey') || isSavingKey('telegramSignInBotApiKey')"
                              @click="handleReset('telegramSignInBotApiKey')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <div>
                          <label class="form-label text-sm fw-semibold">Webhook secret key</label>
                          <input class="form-control" type="text"
                            v-model="draftValues.telegramSignInBotWebhookSecretKey" placeholder="secret" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('telegramSignInBotWebhookSecretKey') || isSavingKey('telegramSignInBotWebhookSecretKey')"
                              @click="handleSave('telegramSignInBotWebhookSecretKey')">
                              <span v-if="isSavingKey('telegramSignInBotWebhookSecretKey')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('telegramSignInBotWebhookSecretKey') || isSavingKey('telegramSignInBotWebhookSecretKey')"
                              @click="handleReset('telegramSignInBotWebhookSecretKey')">
                              Reset
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-lg-6">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">Matching engine</h6>
                        <p class="text-xs text-secondary mb-3">Switch the backend used to run <code>getMatches</code>
                          queries.</p>

                        <div class="form-check form-switch mb-3">
                          <input class="form-check-input" type="checkbox" id="useOpenSearchForMatching"
                            :checked="draftValues.useOpenSearchForMatching === true"
                            @change="assignValue('useOpenSearchForMatching', $event.target.checked)" />
                          <label class="form-check-label text-sm fw-semibold me-2" for="useOpenSearchForMatching">
                            Use OpenSearch for matching
                          </label>
                          <p class="text-xs text-secondary mt-1 mb-0">
                            Off: MongoDB (default). On: OpenSearch.
                          </p>
                        </div>

                        <div class="d-flex gap-2 align-items-center">
                          <button class="btn btn-sm btn-success"
                            :disabled="!hasChanges('useOpenSearchForMatching') || isSavingKey('useOpenSearchForMatching')"
                            @click="handleSave('useOpenSearchForMatching')">
                            <span v-if="isSavingKey('useOpenSearchForMatching')"
                              class="spinner-border spinner-border-sm"></span>
                            <span v-else>Save</span>
                          </button>
                          <button class="btn btn-sm btn-outline-secondary"
                            :disabled="!hasChanges('useOpenSearchForMatching') || isSavingKey('useOpenSearchForMatching')"
                            @click="handleReset('useOpenSearchForMatching')">
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Media -->
              <div v-else-if="activeTab === 'media'">
                <div class="row g-3">
                  <div class="col-12 col-lg-6">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">Media limits</h6>
                        <p class="text-xs text-secondary mb-3">Set maximum upload sizes (bytes).</p>

                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Max image size (bytes)</label>
                          <input class="form-control" type="number" :value="draftValues.maxImageSizeInBytes ?? ''"
                            @input="numericInputHandler('maxImageSizeInBytes', $event.target.value)"
                            placeholder="5242880" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('maxImageSizeInBytes') || isSavingKey('maxImageSizeInBytes')"
                              @click="handleSave('maxImageSizeInBytes')">
                              <span v-if="isSavingKey('maxImageSizeInBytes')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('maxImageSizeInBytes') || isSavingKey('maxImageSizeInBytes')"
                              @click="handleReset('maxImageSizeInBytes')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <div>
                          <label class="form-label text-sm fw-semibold">Max voice size (bytes)</label>
                          <input class="form-control" type="number" :value="draftValues.maxVoiceSizeInBytes ?? ''"
                            @input="numericInputHandler('maxVoiceSizeInBytes', $event.target.value)"
                            placeholder="10485760" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('maxVoiceSizeInBytes') || isSavingKey('maxVoiceSizeInBytes')"
                              @click="handleSave('maxVoiceSizeInBytes')">
                              <span v-if="isSavingKey('maxVoiceSizeInBytes')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('maxVoiceSizeInBytes') || isSavingKey('maxVoiceSizeInBytes')"
                              @click="handleReset('maxVoiceSizeInBytes')">
                              Reset
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Communication -->
              <div v-else-if="activeTab === 'communication'">
                <div class="row g-3">
                  <div class="col-12 col-lg-6" v-for="key in MESSAGE_KEYS" :key="key">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">
                          {{
                            key === "welcomeMessageNotification"
                              ? "Welcome message"
                              : key === "verificationAcceptedMessageNotification"
                                ? "Verification accepted"
                                : key === "verificationRejectedCodeMessageNotification"
                                  ? "Rejection: code document"
                                  : key === "verificationRejectedSelfieMessageNotification"
                                    ? "Rejection: selfie"
                                    : key === "verificationRejectedGestureMessageNotification"
                                      ? "Rejection: gesture"
                                      : key === "verificationRejectedAgeMessageNotification"
                                        ? "Rejection: age"
                                        : key === "walletTopUpMessageNotification"
                                          ? "Wallet top-up"
                                          : key === "walletTopUpRejectedMessageNotification"
                                            ? "Wallet top-up rejected"
                                            : key === "planPurchaseMessageNotification"
                                              ? "Plan purchase thank you"
                                              : key === "warningMessageNotification"
                                                ? "Warning message"
                                                : key
                          }}
                        </h6>
                        <p class="text-xs text-secondary mb-3">Language-specific content.</p>

                        <div v-if="messageLanguages(key).length === 0" class="text-xs text-secondary mb-3">
                          No languages configured. Add one below.
                        </div>

                        <div v-for="lang in messageLanguages(key)" :key="`${key}-${lang}`" class="mb-3">
                          <div class="d-flex justify-content-between align-items-center mb-1">
                            <span class="badge bg-light text-dark border">{{ lang }}</span>
                            <button class="btn btn-link text-danger text-xs p-0" type="button"
                              @click="removeMessageLanguage(key, lang)">
                              Remove
                            </button>
                          </div>
                          <textarea class="form-control" rows="3" :value="draftValues[key]?.[lang] ?? ''"
                            @input="assignValue(key, { ...(draftValues[key] || {}), [lang]: $event.target.value })"
                            placeholder="Message body"></textarea>
                        </div>

                        <div class="input-group input-group-sm mb-2">
                          <select class="form-select" v-model="messageLanguageInputs[key]">
                            <option value="">Add language</option>
                            <option v-for="lang in languageOptions.filter((l) => !messageLanguages(key).includes(l))"
                              :key="lang" :value="lang">
                              {{ lang }}
                            </option>
                          </select>
                          <button class="btn btn-outline-secondary mb-0" type="button"
                            :disabled="!messageLanguageInputs[key]"
                            @click="addMessageLanguage(key, messageLanguageInputs[key])">
                            Add
                          </button>
                        </div>

                        <div class="d-flex gap-2 align-items-center">
                          <button class="btn btn-sm btn-success" :disabled="!hasChanges(key) || isSavingKey(key)"
                            @click="handleSave(key)">
                            <span v-if="isSavingKey(key)" class="spinner-border spinner-border-sm"></span>
                            <span v-else>Save</span>
                          </button>
                          <button class="btn btn-sm btn-outline-secondary"
                            :disabled="!hasChanges(key) || isSavingKey(key)" @click="handleReset(key)">
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Prompt Library -->
              <div v-else-if="activeTab === 'promptLibrary'">
                <div class="row g-3">
                  <div class="col-12 col-lg-8">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">API access</h6>
                        <p class="text-xs text-secondary mb-2">
                          Bearer token external callers (e.g. the GitLab pipeline) must send in the
                          <code>Authorization</code> header to <code>POST /config/prompt-library/reload</code>.
                          Requests are rejected while this is empty.
                        </p>
                        <input class="form-control" type="password" v-model="draftValues.configApiToken"
                          placeholder="••••••••" autocomplete="new-password" />
                        <div class="mt-2 d-flex gap-2 align-items-center">
                          <button class="btn btn-sm btn-success"
                            :disabled="!hasChanges('configApiToken') || isSavingKey('configApiToken')"
                            @click="handleSave('configApiToken')">
                            <span v-if="isSavingKey('configApiToken')" class="spinner-border spinner-border-sm"></span>
                            <span v-else>Save</span>
                          </button>
                          <button class="btn btn-sm btn-outline-secondary"
                            :disabled="!hasChanges('configApiToken') || isSavingKey('configApiToken')"
                            @click="handleReset('configApiToken')">
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-lg-8">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">Reload prompts</h6>
                        <p class="text-xs text-secondary mb-3">
                          Prompts are published to the prompt library bucket by the GitLab pipeline, then read on first
                          use and kept in memory. Reloading drops the cached copies on every server so the next request
                          picks up the current files. Run it after a pipeline publishes new prompts.
                        </p>
                        <button class="btn btn-sm btn-primary" :disabled="!canEditConfig || isReloadingPrompts"
                          @click="handleReloadPrompts">
                          <span v-if="isReloadingPrompts" class="spinner-border spinner-border-sm"></span>
                          <span v-else>Reload prompts</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-lg-8">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">Files</h6>
                        <p class="text-xs text-secondary mb-3">Object key each prompt is read from in the bucket.</p>
                        <div class="table-responsive">
                          <table class="table align-items-center mb-0">
                            <thead>
                              <tr>
                                <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Prompt</th>
                                <th class="text-uppercase text-secondary text-xxs font-weight-bolder">File</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="prompt in PROMPT_LIBRARY_FILES" :key="prompt.path">
                                <td class="text-sm">{{ prompt.label }}</td>
                                <td class="text-sm"><code>{{ prompt.path }}</code></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Email Inbox -->
              <div v-else-if="activeTab === 'emailManagement'">
                <div class="row g-3">
                  <div class="col-12 col-lg-8">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">Inbound webhook</h6>
                        <p class="text-xs text-secondary mb-3">
                          Incoming mail is delivered by the Cloudflare email router worker to
                          <code>POST /email/webhook</code>. Authenticate it with either the signing secret or a static
                          authorization header — requests are rejected while both are empty. Replies are sent through
                          the regular transactional email sender.
                        </p>

                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Webhook secret</label>
                          <p class="text-xs text-secondary mb-2">
                            Must match the worker's <code>WEBHOOK_SECRET</code>. When set, every request is verified by
                            its HMAC signature and the authorization header below is ignored.
                          </p>
                          <input class="form-control" type="password"
                            v-model="draftValues.emailManagementWebhookSecret" placeholder="••••••••"
                            autocomplete="new-password" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('emailManagementWebhookSecret') || isSavingKey('emailManagementWebhookSecret')"
                              @click="handleSave('emailManagementWebhookSecret')">
                              <span v-if="isSavingKey('emailManagementWebhookSecret')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('emailManagementWebhookSecret') || isSavingKey('emailManagementWebhookSecret')"
                              @click="handleReset('emailManagementWebhookSecret')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <div>
                          <label class="form-label text-sm fw-semibold">Authorization header</label>
                          <p class="text-xs text-secondary mb-2">
                            Used only when no webhook secret is set. Must match the worker's
                            <code>WEBHOOK_AUTHORIZATION</code> verbatim, e.g. <code>Bearer …</code>.
                          </p>
                          <input class="form-control" type="password"
                            v-model="draftValues.emailManagementWebhookAuthorization" placeholder="••••••••"
                            autocomplete="new-password" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('emailManagementWebhookAuthorization') || isSavingKey('emailManagementWebhookAuthorization')"
                              @click="handleSave('emailManagementWebhookAuthorization')">
                              <span v-if="isSavingKey('emailManagementWebhookAuthorization')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('emailManagementWebhookAuthorization') || isSavingKey('emailManagementWebhookAuthorization')"
                              @click="handleReset('emailManagementWebhookAuthorization')">
                              Reset
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-lg-8">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">Shared mailboxes</h6>
                        <p class="text-xs text-secondary mb-3">
                          Addresses the whole team can reply from on the Email Inbox page, such as
                          <code>info@…</code> or <code>support@…</code>. Personal addresses do not belong here — each
                          admin can always reply from their own account email, and mail arriving at it is assigned to
                          them automatically. Leave the list empty to fall back to the tenant-wide
                          <code>emailFrom</code> on the Brand tab.
                        </p>

                        <div v-if="(draftValues.emailManagementSenders || []).length"
                          class="d-flex flex-column gap-2 mb-3">
                          <div v-for="sender in draftValues.emailManagementSenders" :key="sender.email"
                            class="input-group input-group-sm">
                            <span class="input-group-text text-xs" style="min-width:220px">{{ sender.email }}</span>
                            <input class="form-control" type="text" placeholder="Display name"
                              :value="sender.name || ''"
                              @input="updateEmailSenderName(sender.email, $event.target.value)" />
                            <button class="btn btn-outline-danger mb-0" type="button"
                              @click="removeEmailSender(sender.email)">
                              &times;
                            </button>
                          </div>
                        </div>
                        <p v-else class="text-xs text-secondary mb-3">No sender addresses added yet.</p>

                        <div class="input-group input-group-sm mb-3">
                          <input class="form-control" type="email" v-model="newSenderEmail"
                            placeholder="nika@example.com" @keyup.enter="addEmailSender" />
                          <input class="form-control" type="text" v-model="newSenderName" placeholder="Display name"
                            @keyup.enter="addEmailSender" />
                          <button class="btn btn-outline-secondary mb-0" type="button" :disabled="!newSenderEmail"
                            @click="addEmailSender">
                            Add
                          </button>
                        </div>

                        <div class="d-flex gap-2 align-items-center">
                          <button class="btn btn-sm btn-success"
                            :disabled="!hasChanges('emailManagementSenders') || isSavingKey('emailManagementSenders')"
                            @click="handleSave('emailManagementSenders')">
                            <span v-if="isSavingKey('emailManagementSenders')"
                              class="spinner-border spinner-border-sm"></span>
                            <span v-else>Save</span>
                          </button>
                          <button class="btn btn-sm btn-outline-secondary"
                            :disabled="!hasChanges('emailManagementSenders') || isSavingKey('emailManagementSenders')"
                            @click="handleReset('emailManagementSenders')">
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              <!-- Compliance AI Response -->
              <div v-else-if="activeTab === 'complianceAiResponse'">
                <div class="row g-3">
                  <div class="col-12 col-lg-8">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">OpenAI model</h6>
                        <p class="text-xs text-secondary mb-3">Model used for AI-assisted complaint resolution. Defaults
                          to <code>gpt-5.4</code> if not set.</p>
                        <input class="form-control" type="text" v-model="draftValues.aiComplaintOpenAiModel"
                          placeholder="e.g. gpt-5.4" />
                        <div class="mt-2 d-flex gap-2 align-items-center">
                          <button class="btn btn-sm btn-success"
                            :disabled="!hasChanges('aiComplaintOpenAiModel') || isSavingKey('aiComplaintOpenAiModel')"
                            @click="handleSave('aiComplaintOpenAiModel')">
                            <span v-if="isSavingKey('aiComplaintOpenAiModel')"
                              class="spinner-border spinner-border-sm"></span>
                            <span v-else>Save</span>
                          </button>
                          <button class="btn btn-sm btn-outline-secondary"
                            :disabled="!hasChanges('aiComplaintOpenAiModel') || isSavingKey('aiComplaintOpenAiModel')"
                            @click="handleReset('aiComplaintOpenAiModel')">
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Referral -->
              <div v-else-if="activeTab === 'referral'">
                <div class="row g-3">
                  <div class="col-12 col-lg-6">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">Referral program</h6>
                        <p class="text-xs text-secondary mb-3">Control referral eligibility and rewards.</p>

                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Max refer count</label>
                          <input class="form-control" type="number" :value="draftValues.referralMaxReferCount ?? ''"
                            @input="numericInputHandler('referralMaxReferCount', $event.target.value)" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('referralMaxReferCount') || isSavingKey('referralMaxReferCount')"
                              @click="handleSave('referralMaxReferCount')">
                              <span v-if="isSavingKey('referralMaxReferCount')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('referralMaxReferCount') || isSavingKey('referralMaxReferCount')"
                              @click="handleReset('referralMaxReferCount')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <div class="form-check form-switch mb-3">
                          <input class="form-check-input" type="checkbox" id="referralVerificationRequired"
                            :checked="draftValues.referralVerificationRequired === true"
                            @change="assignValue('referralVerificationRequired', $event.target.checked)" />
                          <label class="form-check-label text-sm fw-semibold me-2" for="referralVerificationRequired">
                            Verification required
                          </label>
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('referralVerificationRequired') || isSavingKey('referralVerificationRequired')"
                              @click="handleSave('referralVerificationRequired')">
                              <span v-if="isSavingKey('referralVerificationRequired')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('referralVerificationRequired') || isSavingKey('referralVerificationRequired')"
                              @click="handleReset('referralVerificationRequired')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <div class="form-check form-switch mb-3">
                          <input class="form-check-input" type="checkbox" id="referralFacePhotoRequired"
                            :checked="draftValues.referralFacePhotoRequired === true"
                            @change="assignValue('referralFacePhotoRequired', $event.target.checked)" />
                          <label class="form-check-label text-sm fw-semibold me-2" for="referralFacePhotoRequired">
                            Face photo required
                          </label>
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('referralFacePhotoRequired') || isSavingKey('referralFacePhotoRequired')"
                              @click="handleSave('referralFacePhotoRequired')">
                              <span v-if="isSavingKey('referralFacePhotoRequired')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('referralFacePhotoRequired') || isSavingKey('referralFacePhotoRequired')"
                              @click="handleReset('referralFacePhotoRequired')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Reward product ID</label>
                          <input class="form-control" type="text" v-model="draftValues.referralRewardProductId"
                            placeholder="product_123" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('referralRewardProductId') || isSavingKey('referralRewardProductId')"
                              @click="handleSave('referralRewardProductId')">
                              <span v-if="isSavingKey('referralRewardProductId')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('referralRewardProductId') || isSavingKey('referralRewardProductId')"
                              @click="handleReset('referralRewardProductId')">
                              Reset
                            </button>
                          </div>
                        </div>

                        <div>
                          <label class="form-label text-sm fw-semibold">Reward duration (seconds)</label>
                          <input class="form-control" type="number"
                            :value="draftValues.referralRewardProductDuration ?? ''"
                            @input="numericInputHandler('referralRewardProductDuration', $event.target.value)" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('referralRewardProductDuration') || isSavingKey('referralRewardProductDuration')"
                              @click="handleSave('referralRewardProductDuration')">
                              <span v-if="isSavingKey('referralRewardProductDuration')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('referralRewardProductDuration') || isSavingKey('referralRewardProductDuration')"
                              @click="handleReset('referralRewardProductDuration')">
                              Reset
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Payments -->
              <div v-else-if="activeTab === 'payments'">
                <div class="row g-3">
                  <div class="col-12">
                    <div class="card border shadow-sm">
                      <div class="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                          <h6 class="mb-1">Payment methods</h6>
                          <p class="text-xs text-secondary mb-0">
                            Which payment methods each user sees, including per-user and audience rules,
                            is managed on its own page.
                          </p>
                        </div>
                        <router-link to="/payment-methods" class="btn btn-sm btn-outline-success mb-0">
                          Open Payment Methods
                        </router-link>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-lg-6">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">Gift card reseller discount</h6>
                        <p class="text-xs text-secondary mb-3">
                          Discount resellers get below face value, shown in the wallet's gift card tab.
                        </p>

                        <div class="mb-4">
                          <label class="form-label text-sm fw-semibold" for="websiteGiftCardDiscountPercent">
                            Discount (%)
                          </label>
                          <input id="websiteGiftCardDiscountPercent" class="form-control" type="number" min="0"
                            max="99" step="0.01" :value="draftValues.websiteGiftCardDiscountPercent ?? ''"
                            @input="numericInputHandler('websiteGiftCardDiscountPercent', $event.target.value)"
                            placeholder="5" />
                          <div class="mt-2 d-flex gap-2 align-items-center">
                            <button class="btn btn-sm btn-success"
                              :disabled="!hasChanges('websiteGiftCardDiscountPercent') || isSavingKey('websiteGiftCardDiscountPercent')"
                              @click="handleSave('websiteGiftCardDiscountPercent')">
                              <span v-if="isSavingKey('websiteGiftCardDiscountPercent')"
                                class="spinner-border spinner-border-sm"></span>
                              <span v-else>Save</span>
                            </button>
                            <button class="btn btn-sm btn-outline-secondary"
                              :disabled="!hasChanges('websiteGiftCardDiscountPercent') || isSavingKey('websiteGiftCardDiscountPercent')"
                              @click="handleReset('websiteGiftCardDiscountPercent')">
                              Reset
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-12">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">Currency exchange rate</h6>
                        <p class="text-xs text-secondary mb-3">
                          Single, shared exchange rate used to convert invoice amounts.
                        </p>
                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">Rate</label>
                          <input class="form-control" type="number" min="0" step="any"
                            :value="draftValues.exchangeRate ?? ''"
                            @input="numericInputHandler('exchangeRate', $event.target.value)"
                            placeholder="e.g. 580000" />
                        </div>
                        <div class="mt-2 d-flex gap-2 align-items-center">
                          <button class="btn btn-sm btn-success"
                            :disabled="!hasChanges('exchangeRate') || isSavingKey('exchangeRate')"
                            @click="handleSave('exchangeRate')">
                            <span v-if="isSavingKey('exchangeRate')" class="spinner-border spinner-border-sm"></span>
                            <span v-else>Save</span>
                          </button>
                          <button class="btn btn-sm btn-outline-secondary"
                            :disabled="!hasChanges('exchangeRate') || isSavingKey('exchangeRate')"
                            @click="handleReset('exchangeRate')">
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-12">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <h6 class="mb-1">Telegram Stars rate</h6>
                        <p class="text-xs text-secondary mb-3">
                          Value of one Telegram Star in USD cents. Each top-up ($1–$100) is priced in Stars at this rate,
                          rounded up, and locked in when the invoice is created. Leave empty to hide the Telegram Stars tab.
                        </p>
                        <div class="mb-3">
                          <label class="form-label text-sm fw-semibold">USD cents per Star</label>
                          <input class="form-control" type="number" min="0" step="any"
                            :value="draftValues.telegramStarsUsdCentsPerStar ?? ''"
                            @input="numericInputHandler('telegramStarsUsdCentsPerStar', $event.target.value)"
                            placeholder="e.g. 1.3" />
                        </div>
                        <div class="mt-2 d-flex gap-2 align-items-center">
                          <button class="btn btn-sm btn-success"
                            :disabled="!hasChanges('telegramStarsUsdCentsPerStar') || isSavingKey('telegramStarsUsdCentsPerStar')"
                            @click="handleSave('telegramStarsUsdCentsPerStar')">
                            <span v-if="isSavingKey('telegramStarsUsdCentsPerStar')" class="spinner-border spinner-border-sm"></span>
                            <span v-else>Save</span>
                          </button>
                          <button class="btn btn-sm btn-outline-secondary"
                            :disabled="!hasChanges('telegramStarsUsdCentsPerStar') || isSavingKey('telegramStarsUsdCentsPerStar')"
                            @click="handleReset('telegramStarsUsdCentsPerStar')">
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Plans -->
              <div v-else-if="activeTab === 'plans'">
                <div class="row g-3">
                  <div class="col-12">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                          <div>
                            <h6 class="mb-1">Plans</h6>
                            <p class="text-xs text-secondary mb-0">
                              Manage purchasable plans and their feature limits.
                            </p>
                          </div>
                          <div class="d-flex gap-2">
                            <button class="btn btn-sm btn-outline-success mb-0" type="button" :disabled="plansLoading"
                              @click="loadPlans(true)">
                              <span v-if="plansLoading" class="spinner-border spinner-border-sm me-1"></span>
                              Reload
                            </button>
                            <button class="btn btn-sm btn-primary mb-0" type="button" @click="startCreatePlan">
                              New plan
                            </button>
                          </div>
                        </div>

                        <argon-alert v-if="plansError" color="danger" class="mb-3">
                          {{ plansError }}
                        </argon-alert>
                        <argon-alert v-if="plansStatus" :color="plansStatusType" class="mb-3">
                          {{ plansStatus }}
                        </argon-alert>

                        <div v-if="plansLoading" class="text-center py-4">
                          <div class="spinner-border text-success" role="status">
                            <span class="visually-hidden">Loading...</span>
                          </div>
                          <p class="mt-3 mb-0 text-sm text-secondary">Loading plans...</p>
                        </div>

                        <div v-else class="table-responsive">
                          <table class="table align-items-center mb-0">
                            <thead>
                              <tr>
                                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">ID</th>
                                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Name
                                </th>
                                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                  Visibility
                                </th>
                                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status
                                </th>
                                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Features
                                </th>
                                <th class="text-secondary opacity-7"></th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="plan in plans" :key="plan.id">
                                <td class="text-sm fw-semibold">{{ plan.id }}</td>
                                <td>
                                  <div v-if="Object.keys(plan.name || {}).length">
                                    <div
                                      v-for="[lang, label] in Object.entries(plan.name || {}).sort((a, b) => a[0].localeCompare(b[0]))"
                                      :key="`${plan.id}-${lang}`" class="text-sm d-flex align-items-center">
                                      <span class="badge bg-light text-dark border me-2">{{ lang }}</span>
                                      <span>{{ label || "--" }}</span>
                                    </div>
                                  </div>
                                  <div v-else class="text-xs text-secondary">No names set.</div>
                                </td>
                                <td class="text-sm">{{ plan.matchingVisibilityScore }}</td>
                                <td>
                                  <span class="badge" :class="plan.isActive ? 'bg-success' : 'bg-secondary'">
                                    {{ plan.isActive ? 'Active' : 'Disabled' }}
                                  </span>
                                </td>
                                <td>
                                  <div v-if="plan.features.length">
                                    <div v-for="feature in plan.features" :key="`${plan.id}-${feature.payableFeature}`"
                                      class="text-sm d-flex align-items-center mb-1">
                                      <span class="badge bg-light text-dark border me-2">
                                        {{ formatPayableFeature(feature.payableFeature) }}
                                      </span>
                                      <span>{{ feature.windowLimit }}</span>
                                      <span v-if="feature.windowDuration" class="text-xs text-secondary ms-1">
                                        / {{ feature.windowDuration }}
                                      </span>
                                    </div>
                                  </div>
                                  <div v-else class="text-xs text-secondary">No features</div>
                                </td>
                                <td class="text-end">
                                  <button class="btn btn-sm btn-outline-secondary mb-0 me-2" type="button"
                                    @click="startEditFeatures(plan)">
                                    Features
                                  </button>
                                  <button class="btn btn-sm btn-outline-secondary mb-0 me-2" type="button"
                                    @click="startEditOffers(plan)">
                                    Offers
                                  </button>
                                  <button class="btn btn-sm btn-outline-primary mb-0" type="button"
                                    @click="startEditPlan(plan)">
                                    Edit
                                  </button>
                                </td>
                              </tr>
                              <tr v-if="!plans.length">
                                <td colspan="99" class="text-center text-sm text-secondary py-3">
                                  No plans configured yet.
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="activeTab === 'plans' && isPlanModalOpen" class="modal fade show d-block" tabindex="-1"
                  role="dialog" style="background: rgba(0, 0, 0, 0.45);" @click.self="closePlanModal">
                  <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
                    <div class="modal-content">
                      <div class="modal-header">
                        <div>
                          <h6 class="modal-title mb-0">
                            {{ isEditingPlan ? `Edit plan: ${planDraft.id}` : "Create plan" }}
                          </h6>
                          <p class="text-xs text-secondary mb-0">
                            Set plan metadata and products.
                          </p>
                        </div>
                        <button type="button" class="btn-close" aria-label="Close" @click="closePlanModal"></button>
                      </div>
                      <div class="modal-body">
                        <argon-alert v-if="planFormMessage" :color="planFormMessageType" class="mb-3">
                          {{ planFormMessage }}
                        </argon-alert>

                        <div class="row g-3">
                          <div class="col-12 col-md-6">
                            <label class="form-label text-sm fw-semibold">Plan ID</label>
                            <input class="form-control" type="text" v-model="planDraft.id" :disabled="isEditingPlan"
                              placeholder="gold_plan" />
                          </div>
                          <div class="col-12 col-md-2 d-flex align-items-end">
                            <div class="form-check form-switch">
                              <input class="form-check-input" type="checkbox" id="plan-is-active"
                                v-model="planDraft.isActive" />
                              <label class="form-check-label text-sm" for="plan-is-active">Active</label>
                            </div>
                          </div>
                          <div class="col-12 col-md-4">
                            <label class="form-label text-sm fw-semibold">Matching visibility score</label>
                            <input class="form-control" type="number" v-model.number="planDraft.matchingVisibilityScore"
                              min="0" />
                          </div>
                        </div>

                        <div class="row g-3 mt-1">
                        </div>

                        <div class="mt-3">
                          <label class="form-label text-sm fw-semibold">Names</label>
                          <div v-if="planLanguages.length" class="row g-2">
                            <div class="col-12 col-md-6" v-for="lang in planLanguages" :key="`plan-name-${lang}`">
                              <div class="input-group input-group-sm">
                                <span class="input-group-text">{{ lang }}</span>
                                <input class="form-control" type="text" v-model="planDraft.name[lang]"
                                  :placeholder="`Name (${lang})`" />
                              </div>
                            </div>
                          </div>
                          <div v-else class="text-xs text-secondary">
                            Add languages in Brand tab to set localized titles.
                          </div>
                        </div>

                        <div class="mt-3">
                          <label class="form-label text-sm fw-semibold">Product IDs</label>
                          <div v-for="(productId, idx) in planDraft.productIds" :key="`product-${idx}`"
                            class="input-group input-group-sm mb-2">
                            <input class="form-control" type="text" v-model="planDraft.productIds[idx]"
                              placeholder="product_123" />
                            <button class="btn btn-outline-danger mb-0" type="button"
                              :disabled="planDraft.productIds.length === 1" @click="removeProductRow(idx)">
                              Remove
                            </button>
                          </div>
                          <button class="btn btn-sm btn-outline-secondary mb-0" type="button" @click="addProductRow">
                            Add product
                          </button>
                        </div>
                      </div>
                      <div class="modal-footer">
                        <button type="button" class="btn btn-light" :disabled="planSaving" @click="closePlanModal">
                          Close
                        </button>
                        <button class="btn btn-outline-secondary" type="button" :disabled="planSaving"
                          @click="resetPlanForm">
                          Reset
                        </button>
                        <button class="btn btn-primary" type="button" :disabled="planSaving" @click="savePlan">
                          <span v-if="planSaving" class="spinner-border spinner-border-sm me-1"></span>
                          {{ isEditingPlan ? "Update plan" : "Create plan" }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="activeTab === 'plans' && isFeaturesModalOpen" class="modal fade show d-block" tabindex="-1"
                  role="dialog" style="background: rgba(0, 0, 0, 0.45);" @click.self="closeFeaturesModal">
                  <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
                    <div class="modal-content">
                      <div class="modal-header">
                        <div>
                          <h6 class="modal-title mb-0">
                            Edit features: {{ editingFeaturesPlanId }}
                          </h6>
                          <p class="text-xs text-secondary mb-0">
                            Manage the payable feature limits for this plan.
                          </p>
                        </div>
                        <button type="button" class="btn-close" aria-label="Close" @click="closeFeaturesModal"></button>
                      </div>
                      <div class="modal-body">
                        <argon-alert v-if="featuresFormMessage" :color="featuresFormMessageType" class="mb-3">
                          {{ featuresFormMessage }}
                        </argon-alert>

                        <div v-if="featuresDraft.features.length === 0" class="text-xs text-secondary mb-2">
                          No features added yet.
                        </div>
                        <div v-for="(feature, idx) in featuresDraft.features"
                          :key="`feature-${feature.payableFeature}-${idx}`" class="border rounded p-2 mb-2">
                          <div class="d-flex justify-content-between align-items-center mb-2">
                            <span class="badge bg-light text-dark border">
                              {{ formatPayableFeature(feature.payableFeature) }}
                            </span>
                            <button class="btn btn-link text-danger text-xs p-0" type="button"
                              @click="removeFeatureRow(idx)">
                              Remove
                            </button>
                          </div>
                          <div class="row g-2">
                            <div class="col-12 col-md-6">
                              <label class="form-label text-xs text-secondary mb-1">Window limit</label>
                              <input class="form-control form-control-sm" type="number"
                                v-model.number="feature.windowLimit" @wheel="$event.target.blur()" />
                            </div>
                            <div class="col-12 col-md-6">
                              <label class="form-label text-xs text-secondary mb-1">Window duration (ISO,
                                optional)</label>
                              <input class="form-control form-control-sm" type="text" v-model="feature.windowDuration"
                                placeholder="PT24H" />
                            </div>
                          </div>
                        </div>
                        <div class="input-group input-group-sm mt-2" style="max-width: 360px">
                          <select class="form-select" v-model="selectedFeatureToAdd">
                            <option value="">Add feature</option>
                            <option v-for="feature in availableFeatureOptions" :key="feature" :value="feature">
                              {{ formatPayableFeature(feature) }}
                            </option>
                          </select>
                          <button class="btn btn-outline-secondary mb-0" type="button" :disabled="!selectedFeatureToAdd"
                            @click="addFeatureRow(selectedFeatureToAdd)">
                            Add
                          </button>
                        </div>
                      </div>
                      <div class="modal-footer">
                        <button type="button" class="btn btn-light" :disabled="featuresSaving"
                          @click="closeFeaturesModal">
                          Close
                        </button>
                        <button class="btn btn-outline-secondary" type="button" :disabled="featuresSaving"
                          @click="resetFeaturesForm">
                          Reset
                        </button>
                        <button class="btn btn-primary" type="button" :disabled="featuresSaving" @click="saveFeatures">
                          <span v-if="featuresSaving" class="spinner-border spinner-border-sm me-1"></span>
                          Save features
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="activeTab === 'plans' && isOffersModalOpen" class="modal fade show d-block" tabindex="-1"
                  role="dialog" style="background: rgba(0, 0, 0, 0.45);" @click.self="closeOffersModal">
                  <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
                    <div class="modal-content">
                      <div class="modal-header">
                        <div>
                          <h6 class="modal-title mb-0">
                            Edit offers: {{ editingOffersPlanId }}
                          </h6>
                          <p class="text-xs text-secondary mb-0">
                            Manage the default purchasable offers for this plan.
                          </p>
                        </div>
                        <button type="button" class="btn-close" aria-label="Close" @click="closeOffersModal"></button>
                      </div>
                      <div class="modal-body">
                        <argon-alert v-if="offersFormMessage" :color="offersFormMessageType" class="mb-3">
                          {{ offersFormMessage }}
                        </argon-alert>

                        <div v-if="offersDraft.offers.length === 0" class="text-xs text-secondary mb-2">
                          No offers added yet.
                        </div>
                        <div v-for="(offer, idx) in offersDraft.offers" :key="`offer-${idx}`"
                          class="border rounded p-2 mb-2">
                          <div class="d-flex justify-content-between align-items-center mb-2">
                            <span class="badge bg-light text-dark border">Offer {{ idx + 1 }}</span>
                            <button class="btn btn-link text-danger text-xs p-0" type="button"
                              @click="removeOfferRow(idx)">
                              Remove
                            </button>
                          </div>
                          <div class="row g-2">
                            <div class="col-12 col-md-6">
                              <label class="form-label text-xs text-secondary mb-1">Offer ID</label>
                              <input class="form-control form-control-sm" type="text" v-model="offer.id"
                                placeholder="gold_plan_monthly" />
                            </div>
                            <div class="col-12 col-md-6">
                              <label class="form-label text-xs text-secondary mb-1">Title</label>
                              <input class="form-control form-control-sm" type="text" v-model="offer.title" />
                            </div>
                            <div class="col-12">
                              <label class="form-label text-xs text-secondary mb-1">Description</label>
                              <input class="form-control form-control-sm" type="text" v-model="offer.description" />
                            </div>
                            <div class="col-12">
                              <label class="form-label text-xs text-secondary mb-1">Subscription period (ISO,
                                optional)</label>
                              <input class="form-control form-control-sm" type="text" v-model="offer.subscriptionPeriod"
                                placeholder="P1M" />
                            </div>
                            <div class="col-12">
                              <label class="form-label text-xs text-secondary mb-1">Prices</label>
                              <div v-if="(offer.prices || []).length === 0" class="text-xs text-secondary mb-2">
                                No prices added yet — this offer won't be purchasable until it has at least one.
                              </div>
                              <div v-for="(price, priceIdx) in offer.prices" :key="`offer-${idx}-price-${priceIdx}`"
                                class="row g-2 align-items-end mb-2">
                                <div class="col-5">
                                  <label class="form-label text-xs text-secondary mb-1">Currency</label>
                                  <select class="form-control form-control-sm" v-model="price.currencyCode">
                                    <option value="USD">USD</option>
                                    <option value="IRR">IRR</option>
                                  </select>
                                </div>
                                <div class="col-5">
                                  <label class="form-label text-xs text-secondary mb-1">Amount (minor units, e.g.
                                    cents)</label>
                                  <input class="form-control form-control-sm" type="number" v-model.number="price.price"
                                    min="0" />
                                </div>
                                <div class="col-2">
                                  <button class="btn btn-link text-danger text-xs p-0" type="button"
                                    @click="removeOfferPriceRow(idx, priceIdx)">
                                    Remove
                                  </button>
                                </div>
                              </div>
                              <button class="btn btn-sm btn-outline-secondary mb-0" type="button"
                                @click="addOfferPriceRow(idx)">
                                Add price
                              </button>
                            </div>
                          </div>
                        </div>
                        <button class="btn btn-sm btn-outline-secondary mb-0" type="button" @click="addOfferRow">
                          Add offer
                        </button>
                      </div>
                      <div class="modal-footer">
                        <button type="button" class="btn btn-light" :disabled="offersSaving" @click="closeOffersModal">
                          Close
                        </button>
                        <button class="btn btn-outline-secondary" type="button" :disabled="offersSaving"
                          @click="resetOffersForm">
                          Reset
                        </button>
                        <button class="btn btn-primary" type="button" :disabled="offersSaving" @click="saveOffers">
                          <span v-if="offersSaving" class="spinner-border spinner-border-sm me-1"></span>
                          Save offers
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Profile attributes -->
              <div v-else-if="activeTab === 'profileAttributes'">
                <div class="row g-3">
                  <div class="col-12">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                          <div>
                            <h6 class="mb-1">Profile attributes</h6>
                            <p class="text-xs text-secondary mb-0">
                              View and edit profile attribute metadata.
                            </p>
                          </div>
                          <button class="btn btn-sm btn-outline-success mb-0" type="button"
                            :disabled="profileAttributesLoading" @click="loadProfileAttributes">
                            <span v-if="profileAttributesLoading" class="spinner-border spinner-border-sm me-1"></span>
                            Reload
                          </button>
                        </div>

                        <argon-alert v-if="profileAttributesError" color="danger" class="mb-3">
                          {{ profileAttributesError }}
                        </argon-alert>
                        <argon-alert v-if="profileAttributeStatus" :color="profileAttributeStatusType" class="mb-3">
                          {{ profileAttributeStatus }}
                        </argon-alert>

                        <div class="row">
                          <div class="col-12 col-lg-4">
                            <div class="list-group" style="overflow-y: auto">
                              <button type="button" class="list-group-item list-group-item-action"
                                v-for="attr in profileAttributes" :key="attr.name"
                                :class="selectedProfileAttribute?.name === attr.name ? 'active' : ''"
                                :disabled="profileAttributesLoading" @click="applyProfileAttribute(attr)">
                                <div class="d-flex justify-content-between align-items-center">
                                  <div>
                                    <div class="fw-semibold">{{ attr.name }}</div>
                                    <small class="text-secondary">{{ attr.type }} | {{ attr.isMultiple ? 'Multiple' :
                                      'Single'
                                    }}</small>
                                  </div>
                                </div>
                              </button>
                              <div v-if="!profileAttributes.length && !profileAttributesLoading"
                                class="text-sm text-secondary p-3">
                                No attributes found.
                              </div>
                            </div>
                          </div>

                          <div class="col-12 col-lg-8 mt-3 mt-lg-0">
                            <div v-if="profileAttributesLoading" class="text-center py-4">
                              <div class="spinner-border text-success" role="status">
                                <span class="visually-hidden">Loading...</span>
                              </div>
                              <p class="mt-3 mb-0 text-sm text-secondary">Loading profile attributes...</p>
                            </div>

                            <div v-else-if="selectedProfileAttribute">
                              <div class="mb-2">
                                <span class="badge bg-light text-dark border me-2">Name: {{
                                  selectedProfileAttribute.name
                                }}</span>
                                <span class="badge bg-light text-dark border me-2">Type: {{
                                  selectedProfileAttribute.type
                                }}</span>
                                <span class="badge bg-light text-dark border">Multiple: {{
                                  selectedProfileAttribute.isMultiple
                                    ? 'Yes' : 'No' }}</span>
                              </div>

                              <div class="mb-3">
                                <label class="form-label text-sm fw-semibold">Description</label>
                                <textarea class="form-control" rows="2"
                                  v-model="profileAttributeDraft.description"></textarea>
                              </div>
                              <div class="row g-3">
                                <div class="col-12 col-md-6">
                                  <label class="form-label text-sm fw-semibold">Match weight</label>
                                  <input class="form-control" type="number"
                                    v-model="profileAttributeDraft.matchWeight" />
                                </div>
                                <div class="col-12 col-md-6">
                                  <label class="form-label text-sm fw-semibold">Profile quality weight</label>
                                  <input class="form-control" type="number"
                                    v-model="profileAttributeDraft.profileQualityWeight" />
                                </div>
                              </div>
                              <div class="form-check form-switch my-3">
                                <input class="form-check-input" type="checkbox" id="useForBioSuggestions"
                                  v-model="profileAttributeDraft.useForBioSuggestions" />
                                <label class="form-check-label" for="useForBioSuggestions">
                                  Use for bio suggestions
                                </label>
                              </div>
                              <div class="mb-3">
                                <label class="form-label text-sm fw-semibold">Options (key: value per line)</label>
                                <textarea class="form-control" rows="6" :value="profileAttributeDraft.options"
                                  readonly></textarea>
                                <div class="form-text">
                                  Options are managed via CSV: upload
                                  <code>profile_attribute_options/{{ selectedProfileAttribute?.name }}.csv</code>
                                  to the internal bucket and run
                                  <code>replace_profile_attribute_options {{ selectedProfileAttribute?.name }}</code>.
                                </div>
                              </div>
                              <div class="d-flex gap-2">
                                <button class="btn btn-sm btn-success" type="button"
                                  :disabled="profileAttributesLoading" @click="saveProfileAttribute">
                                  Save
                                </button>
                                <button class="btn btn-sm btn-outline-secondary" type="button"
                                  :disabled="profileAttributesLoading"
                                  @click="applyProfileAttribute(selectedProfileAttribute)">
                                  Reset
                                </button>
                              </div>
                            </div>

                            <div v-else class="text-sm text-secondary">
                              Select an attribute to edit its details.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Email templates -->
              <div v-else-if="activeTab === 'emailTemplates'">
                <div class="row g-3">
                  <div class="col-12">
                    <div class="card border shadow-sm h-100">
                      <div class="card-body">
                        <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                          <div>
                            <h6 class="mb-1">Email templates</h6>
                            <p class="text-xs text-secondary mb-0">
                              Edit subjects and bodies for transactional emails.
                            </p>
                          </div>
                          <button class="btn btn-sm btn-outline-success mb-0" type="button"
                            :disabled="isLoadingTemplate" @click="loadEmailTemplates">
                            <span v-if="isLoadingTemplate" class="spinner-border spinner-border-sm me-1"></span>
                            Reload
                          </button>
                        </div>

                        <div class="row">
                          <div class="col-12 col-md-4">
                            <div class="list-group" style="overflow-y: auto">
                              <button type="button"
                                class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                v-for="template in emailTemplates" :key="`${template.name}-${template.language}`"
                                :class="selectedTemplateName === template.name && selectedTemplateLanguage === template.language ? 'active' : ''"
                                :disabled="isLoadingTemplate"
                                @click="() => { selectedTemplateName = template.name; selectedTemplateLanguage = template.language; applyTemplateFromList(); }">
                                <div class="d-flex flex-column text-start">
                                  <span class="fw-semibold">{{ template.name }}</span>
                                  <small class="text-secondary">Lang: {{ template.language }}</small>
                                </div>
                              </button>
                              <div v-if="!emailTemplates.length" class="text-sm text-secondary p-3">
                                No templates loaded.
                              </div>
                            </div>
                          </div>

                          <div class="col-12 col-md-8 mt-3 mt-md-0">
                            <argon-alert v-if="templateStatusMessage" :color="templateStatusType" class="mb-3">
                              {{ templateStatusMessage }}
                            </argon-alert>
                            <argon-alert v-if="templateErrorMessage" color="danger" class="mb-3">
                              {{ templateErrorMessage }}
                            </argon-alert>

                            <div v-if="isLoadingTemplate" class="text-center py-4">
                              <div class="spinner-border text-success" role="status">
                                <span class="visually-hidden">Loading...</span>
                              </div>
                              <p class="mt-3 mb-0 text-sm text-secondary">Loading template...</p>
                            </div>

                            <div v-else>
                              <div class="mb-3 d-flex flex-wrap gap-2 align-items-center">
                                <span class="badge bg-light text-dark border">Name: {{ selectedTemplateName || "--"
                                }}</span>
                                <span class="badge bg-light text-dark border">Language: {{ selectedTemplateLanguage ||
                                  "--"
                                }}</span>
                              </div>
                              <div class="mb-3">
                                <label class="form-label text-sm fw-semibold">Subject</label>
                                <input class="form-control" type="text" v-model="emailTemplate.subject"
                                  placeholder="Subject" />
                              </div>
                              <div class="mb-3">
                                <label class="form-label text-sm fw-semibold">Text body</label>
                                <textarea class="form-control" rows="4" v-model="emailTemplate.textBody"
                                  placeholder="Plain text body"></textarea>
                              </div>
                              <div class="mb-3">
                                <label class="form-label text-sm fw-semibold">HTML body</label>
                                <textarea class="form-control" rows="8" v-model="emailTemplate.htmlBody"
                                  placeholder="<p>Hello...</p>"></textarea>
                              </div>
                              <div class="d-flex gap-2">
                                <button class="btn btn-sm btn-success" type="button"
                                  :disabled="isLoadingTemplate || !selectedTemplateName || !selectedTemplateLanguage || !hasTemplateChanges"
                                  @click="saveEmailTemplate">
                                  <span v-if="isLoadingTemplate" class="spinner-border spinner-border-sm me-1"></span>
                                  Save template
                                </button>
                                <button class="btn btn-sm btn-outline-secondary" type="button"
                                  :disabled="isLoadingTemplate || !hasTemplateChanges" @click="resetEmailTemplate">
                                  Reset
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
